/**
 * The English catalogue is the source of truth: every other locale is typed
 * against it, so adding a key here and forgetting it elsewhere fails the build.
 *
 * Keys are `area.thing`. Plurals use `_one` / `_other` and are picked
 * automatically when a `count` variable is passed.
 */
export const en = {
  /* ------------------------------------------------------------- general */
  'app.name': 'Kolibri',
  'action.save': 'Save',
  'action.apply': 'Apply',
  'action.clear': 'Clear',
  'action.cancel': 'Cancel',
  'action.delete': 'Delete',
  'action.undo': 'Undo',
  'action.remove': 'Remove',
  'action.close': 'Close',
  'action.edit': 'Edit',
  'action.done': 'Done',
  'action.open': 'Open',
  'action.create': 'Create',
  'action.add': 'Add',
  'action.restore': 'Restore',
  'action.copyLink': 'Copy link',
  'action.copy': 'Copy',
  'action.archive': 'Archive',
  'action.unarchive': 'Unarchive',
  'action.working': 'Working…',
  'action.confirmTitle': 'Are you sure?',
  'common.untitled': 'Untitled',
  'common.nobody': 'Nobody',
  'common.someone': 'Someone',
  'common.none': 'None',
  'common.name': 'Name',
  'common.empty': 'Empty',
  'common.today': 'Today',
  'common.filterPlaceholder': 'Filter…',
  'common.nothingHere': 'Nothing here',
  'common.somethingWentWrong': 'Something went wrong',
  'common.networkUnavailable': 'Network unavailable',
  'common.copied': 'Link copied',
  'common.count_one': '{count} item',
  'common.count_other': '{count} items',

  /* ---------------------------------------------------------- navigation */
  'nav.myWork': 'My work',
  'nav.inbox': 'Inbox',
  'nav.search': 'Search',
  'nav.chat': 'Chat',
  'nav.pages': 'Pages',
  'nav.teams': 'Teams',
  'nav.trail': 'Where you are',
  'icon.choose': 'Choose an icon',
  'icon.none': 'No icon',
  'nav.backToList': 'Back to the list',
  'nav.projects': 'Projects',
  'nav.more': 'More',
  /* The bottom bar's words. Shorter than the sidebar's, because six items
     share the width of a phone. */
  'nav.bar.myWork': 'My work',
  'nav.bar.inbox': 'Inbox',
  'nav.bar.chat': 'Chat',
  'nav.bar.search': 'Search',
  'nav.bar.more': 'More',
  'nav.mail': 'Mail',
  'nav.secrets': 'Secrets',
  'nav.guide': 'Guide',
  'nav.settings': 'Settings',
  'nav.newTask': 'New task',
  'nav.sidebar': 'Main navigation',
  'nav.foldSidebar': 'Collapse the sidebar',
  'nav.unfoldSidebar': 'Expand the sidebar',
  'nav.tabbar': 'Sections',
  'nav.newProject': 'New project',
  'nav.moveToTop': 'Drop here to move it out of every project',
  'nav.expand': 'Show what is inside {name}',
  'nav.collapse': 'Fold {name}',
  'nav.projectSettings': 'Project settings',
  'project.newSub': 'New sub-project',
  'nav.firstProject': 'Create your first project',
  'nav.account': 'Account',
  'nav.signOut': 'Sign out',
  'nav.switchWorkspace': 'Switch workspace',
  'nav.newWorkspace': 'New workspace…',
  'nav.theme': 'Theme: {theme}',
  'nav.workspaces': 'Workspaces',

  /* --------------------------------------------------------- sync status */
  'sync.synced': 'Synced',
  'sync.syncing': 'Syncing {count}',
  'sync.offline': 'Offline',
  'sync.offlineQueued': 'Offline · {count} queued',
  'sync.issue': 'Sync issue',
  'sync.now': 'Sync now',
  'sync.lastSynced': 'Last synced {time}',
  // Only said when the two clocks are a minute or more apart, which is the
  // point at which the difference could have changed a word on screen.
  'sync.clockAhead': 'This device’s clock is {amount} ahead of the server’s. Times are counted on the server’s.',
  'sync.clockBehind': 'This device’s clock is {amount} behind the server’s. Times are counted on the server’s.',
  'sync.sessionExpired': 'Session expired — please sign in again',
  'sync.failed': 'Sync failed',

  /* ---------------------------------------------------------- priorities */
  'priority.urgent': 'Urgent',
  'priority.high': 'High',
  'priority.medium': 'Medium',
  'priority.low': 'Low',
  'priority.none': 'No priority',

  /* -------------------------------------------------------- state groups */
  'group.backlog': 'Backlog',
  'group.unstarted': 'Todo',
  'group.started': 'In progress',
  'group.completed': 'Done',
  'group.cancelled': 'Cancelled',

  /* --------------------------------------------------------------- tasks */
  'task.title': 'Task',
  'task.state': 'State',
  'task.noState': 'No state',
  'task.priority': 'Priority',
  'task.assignees': 'Assignees',
  'task.unassigned': 'Unassigned',
  'task.labels': 'Labels',
  'task.labelCount_one': '{count} label',
  'task.labelCount_other': '{count} labels',
  'task.noLabelsYet': 'No labels in this project yet',
  'task.cycle': 'Cycle',
  'task.noCycle': 'No cycle',
  'task.module': 'Module',
  'task.noModule': 'No module',
  'task.due': 'Due',
  'task.estimate': 'Estimate',
  'task.repeats': 'Repeats',
  'task.repeatsNever': 'Once',
  'task.repeatsDaily': 'Every day',
  'task.repeatsWeekly': 'Every week',
  'task.repeatsFortnightly': 'Every two weeks',
  'task.repeatsMonthly': 'Every month',
  'task.repeatsHint': 'The next one is created when this one is finished, not when the date passes.',
  'task.subtasks': 'Sub-tasks',
  'task.parent': 'Parent',
  'task.noParent': 'No parent',
  'task.noParentCandidates': 'Nothing in this project could be its parent',
  'task.movedToProject': '{task} moved to {project}',
  'task.movedWithSubtasks_one': '{task} and its sub-task moved to {project}',
  'task.movedWithSubtasks_other': '{task} and its {count} sub-tasks moved to {project}',
  'task.moveToProject': 'Move to project',
  'task.project': 'Project',
  'task.noProjectTargets': 'There is no other project to move it to',
  'task.addSubtask': 'Add a sub-task',
  'task.description': 'Description',
  'attachment.noneYet': 'No files yet — drop one here, or use Attach.',
  'attachment.attach': 'Attach',
  'attachment.none': 'No files.',
  'attachment.uploading': 'Uploading…',
  'attachment.remove': 'Remove',
  'attachment.removeConfirm': 'Remove {name}? It goes to the trash and can be restored. The stored file itself is shared and is not deleted.',
  'attachment.kb': '{size} KB',
  'attachment.files': 'Files',
  'task.comments': 'Comments',
  'task.activity': 'Activity',
  'task.commentPlaceholder': 'Leave a comment… (⌘↵ to send)',
  'task.comment': 'Comment',
  'task.react': 'React',
  'task.subscribe': 'Tell me about this task',
  'task.unsubscribe': 'Stop telling me about it',
  'task.deleteComment': 'Delete this comment?',
  'task.deleteCommentLabel': 'Delete this comment',
  'task.noActivity': 'No activity recorded yet.',
  'task.activityCreated': 'created this task',
  'task.activityChanged': 'changed {field}',
  'task.activitySomething': 'something',
  'task.activityDeleted': 'deleted this task',
  'task.activityRestored': 'restored this task',
  'task.addDescription': 'Add a description…',

  /* A model reading a task back — see components/task-review.tsx */
  'review.title': 'Review',
  'review.ask': 'Ask for a review',
  'review.again': 'Review again',
  'review.sends': 'Sends this task’s project, title, description and labels to {provider}. Nothing else leaves this instance.',
  'review.apply': 'Use this',
  'review.applied': 'Applied — it is an ordinary edit now, and can be edited again.',
  'review.appliedShort': 'Used',
  'review.showProposal': 'Show the suggested text',
  'review.hideProposal': 'Hide the suggested text',
  'review.questions': 'Only a person can answer these',
  'review.questionsIntro': 'A few things about this task that are not clear yet:',
  'review.askThem': 'Ask in a comment',
  'review.asked': 'Posted as a comment — whoever is subscribed will hear about it.',
  'review.askedShort': 'Asked',
  'review.stale': 'The task has changed since this was written.',
  'review.by': 'Reviewed by {model}. Nothing was changed.',
  'review.failed': 'The review did not come back.',
  'task.gone': 'This task is gone',
  'task.goneHint': 'It may have been deleted on another device.',
  'task.deleteConfirm': 'Delete {identifier}? It can be restored under Settings → Data.',
  'task.deleteConfirmSubtasks_one': 'Delete {identifier}? Its sub-task stays, without a parent until this one is restored under Settings → Data.',
  'task.deleteConfirmSubtasks_other': 'Delete {identifier}? Its {count} sub-tasks stay, without a parent until this one is restored under Settings → Data.',
  'task.deleted': '{identifier} deleted',
  'task.delete': 'Delete task',
  // Said with the person where one is known; `task.createdUpdated` is what
  // is left when the creator has left the workspace.
  'task.createdByUpdated': 'Created by {by} on {created} · updated {updated}',
  'task.createdUpdated': 'Created {created} · updated {updated}',
  'task.moveTo': 'Move to',
  'task.subtaskCount': '{done}/{total}',

  /* ---------------------------------------------------------- relations */
  'relation.title': 'Relations',
  'relation.link': 'Link task',
  'relation.none': 'Nothing linked yet.',
  'relation.noCandidates': 'No other tasks to link to',
  'relation.lag': 'Wait',
  'relation.lagHint': 'Working days between this finishing and the other starting — the paint has to dry. Never negative: a task may not start before what blocks it has finished.',
  'relation.remove': 'Remove link',
  'relation.blocks': 'Blocks',
  'relation.blocked_by': 'Blocked by',
  'relation.relates_to': 'Relates to',
  'relation.duplicates': 'Duplicates',
  'relation.duplicated_by': 'Duplicated by',

  /* ------------------------------------------------------------ quick add */
  'quickAdd.title': 'New task',
  'quickAdd.placeholder': 'What needs doing?',
  'quickAdd.read': 'Read from the line:',
  'quickAdd.project': 'Project',
  'quickAdd.assign': 'Assign',
  'quickAdd.assigned': '{count} assigned',
  'quickAdd.addDescription': 'Add description',
  'quickAdd.descriptionPlaceholder': 'Add context, acceptance criteria, links…',
  'quickAdd.saveAndNew': 'Save & new',
  'quickAdd.saveAndNewHint': 'Create and keep the form open',
  'quickAdd.create': 'Create task',
  'quickAdd.needProject': 'Create a project first — tasks live inside projects.',
  'quickAdd.added': 'Added to {project}',

  /* --------------------------------------------------------------- views */
  'view.list': 'List',
  'view.board': 'Board',
  'view.calendar': 'Calendar',
  'view.table': 'Table',
  'view.filter': 'Filter',
  'view.display': 'Display',
  'view.groupBy': 'Group by',
  'view.orderBy': 'Order by',
  'view.noGrouping': 'No grouping',
  'view.clearFilters': 'Clear all filters',
  'query.title': 'Filter as text',
  'query.apply': 'Apply',
  'query.placeholder': '@person, #label or state != Done',
  'query.hint': '@ a person, # a label, + a project — or a condition like state != Done. Plain words search the text.',
  'query.help': 'Clauses join with AND. Use `in (a, b)` for several answers to one field, and `!=` or `not in` to exclude. Bare words search the text, each as a prefix — `"a phrase"` keeps the words together, `-word` leaves out what carries it, and a task number like WEB-12 finds that task. OR between two different fields is not something a saved view can hold.',
  'query.fieldsKept': 'This filter also has custom-field answers. They have no syntax here yet, so they are kept as they are rather than lost.',
  'view.reset': 'Reset',
  'view.showCompleted': 'Show completed',
  'view.hideCompleted': 'Hide completed',
  'view.groupState': 'State',
  'view.groupPriority': 'Priority',
  'view.groupAssignee': 'Assignee',
  'view.groupLabel': 'Label',
  'view.groupCycle': 'Cycle',
  'view.groupProject': 'Project',
  'view.orderManual': 'manual',
  'view.orderPriority': 'priority',
  'view.orderDueDate': 'due date',
  'view.orderUpdatedAt': 'last updated',
  'view.orderTitle': 'title',
  'view.allTasks': 'All tasks',
  'view.noLabel': 'No label',
  'view.emptyTitle': 'Nothing here yet',
  'view.emptyHint': 'Create a task to get started — it works offline too.',
  'view.previousMonth': 'Previous month',
  'view.nextMonth': 'Next month',
  'view.weekdayMon': 'Mon',
  'view.weekdayTue': 'Tue',
  'view.weekdayWed': 'Wed',
  'view.weekdayThu': 'Thu',
  'view.weekdayFri': 'Fri',
  'view.weekdaySat': 'Sat',
  'view.weekdaySun': 'Sun',
  'time.title': 'Time',
  'time.start': 'Start',
  'time.stop': 'Stop',
  'time.log': 'Log',
  'time.logged': 'Logged {amount}',
  'time.stopped': 'Stopped — {amount}',
  'time.running': 'running',
  'time.runningFor': 'Running — {amount} so far.',
  'time.runningElsewhere': 'A timer is running on another task. Starting one here stops that one.',
  'time.switchHint': 'This stops the timer running elsewhere',
  'time.amount': 'How long',
  'time.amountPlaceholder': '1h 30m',
  'time.date': 'Day',
  'time.note': 'What for',
  'time.notePlaceholder': 'What did this go on?',
  'time.deleteEntry': 'Delete this entry',
  'time.deleteConfirm': 'Delete this entry of {amount}?',
  'time.total': 'Logged in total',
  'time.noneYet': 'No time logged on this project yet.',
  'time.tab': 'Time',
  'table.id': 'ID',
  'table.title': 'Title',
  'table.state': 'State',
  'table.assignees': 'Assignees',
  'table.priority': 'Priority',
  'table.due': 'Due',
  'table.estimate': 'Est.',
  'table.labels': 'Labels',
  'table.createdBy': 'Created by',
  'table.updated': 'Updated',
  'insights.tab': 'Insights',
  'insights.open': 'Open',
  'insights.ofTotal': 'of {count} in the project',
  'insights.completed30': 'Finished',
  'insights.last30': 'in the last 30 days',
  'insights.cycleTime': 'Typical time to finish',
  'insights.days': '{count} days',
  'insights.underADay': 'under a day',
  'insights.medianHint': 'middle of the last 30 days',
  'insights.timeLogged': 'Time logged',
  'insights.throughput': 'How much gets finished',
  'insights.throughputHint': 'Tasks reaching a done state, by week. Steady beats spiky — a tall column after three empty ones usually means work was finished long before it was marked.',
  'insights.throughputCaption': 'Tasks finished per week, last twelve weeks',
  'insights.burnUp': 'Burn-up: {name}',
  'insights.burnUpHint': 'Two lines: everything in the cycle, and everything finished. They meet when the cycle is done. Scope climbing mid-cycle is work being added, which is worth seeing rather than hiding.',
  'insights.burnUpCaption': 'Scope and completed tasks over the cycle {name}',
  'insights.scope': 'In the cycle',
  'insights.done': 'Finished',
  'insights.byLabel': 'By label',
  'insights.byLabelHint': 'A task may wear several, so these add up to more than the project has tasks.',
  'insights.unlabelled': 'No label',
  'insights.byPerson': 'Open work per person',
  'insights.byPersonHint': 'Unfinished tasks only, and a task with two people on it counts for both.',
  'insights.tableView': 'The same numbers as a table',
  'insights.week': 'Week of',
  'insights.day': 'Day',
  'insights.completed': 'Finished',
  'insights.tasks': 'Tasks',
  'insights.emptyTitle': 'Nothing to count yet',
  'insights.emptyHint': 'These are all worked out from the tasks in the project. Add a few and come back.',
  /* ----------------------------------------------------------- the estate */
  'nav.infrastructure': 'Infrastructure',
  'settings.tabEstate': 'Infrastructure',
  'workspace.featureEstate': 'Infrastructure register',
  'workspace.featureEstateHint': 'Servers, the instances on them, SaaS subscriptions and who you buy them from — what runs today, what will run later, and the documented steps between the two.',
  'estate.title': 'Infrastructure',
  'estate.tabRegister': 'Register',
  'estate.tabLandscape': 'Landscape',
  'estate.tabMoves': 'Moves',
  'estate.tabVendors': 'Vendors',
  'estate.offTitle': 'The register is switched off',
  'estate.offHint': 'An admin can switch it on in Settings → Workspace. Nothing is lost while it is off.',

  'estate.name': 'Name',
  'estate.namePlaceholder': 'db-01',
  'estate.component': 'Component',
  'estate.components': 'Components',
  'estate.addComponent': 'Add a component',
  'estate.kindLabel': 'Kind',
  'estate.environment': 'Environment',
  'estate.allEnvironments': 'Every environment',
  'estate.statusLabel': 'Status',
  'estate.vendor': 'Vendor',
  'estate.noVendor': 'Nobody in particular',
  'estate.runsOn': 'Runs on',
  'estate.nothing': 'Nothing — it stands on its own',
  'estate.liveFrom': 'In use from',
  'estate.liveUntil': 'Until',
  'estate.window': 'In use',
  'estate.datesHint': 'These dates, not the status, decide which landscape it appears in. Leave the end empty for something with no plan to retire it.',
  'estate.undatedWarning': 'Planned with no start date, so it appears in no landscape — not today’s and not any future one. Give it a date to put it on the map.',
  'estate.location': 'Where it is',
  'estate.locationPlaceholder': 'fr-par-1, rack 4',
  'estate.reference': 'Known as',
  'estate.referencePlaceholder': 'db-01.internal',
  'estate.owner': 'Looked after by',
  'estate.dependsOn': 'Projects that depend on it',
  'estate.dependsOnHint': 'Who breaks if it goes. Not a cost split — that lives on the budget line.',
  'estate.chargedTo': 'Charged to',
  'estate.notBudgeted': 'Not budgeted',
  'estate.chargedToHint': 'The plan line this is paid out of. The budget then shows what the register says against what was planned.',
  'estate.budgeted': 'in a budget',
  'estate.cost': 'Cost',
  'estate.perYear': '/year',
  'estate.once': 'once',
  'estate.oneOffHint': 'Bought rather than rented — kept out of the yearly run rate',
  'estate.deleteComponent': 'Delete “{name}”? Anything running on it stays, at the top of the tree.',
  'estate.showRetired': 'Include retired',
  'estate.running': 'Running today',
  'estate.runningHint': 'Components in the landscape right now',
  'estate.annualRunRate': 'A year',
  'estate.bought': 'Bought',
  'estate.unpriced': '{count} with no price',
  'estate.emptyTitle': 'Nothing written down yet',
  'estate.emptyHint': 'A register is what runs and what it costs: machines, the instances on them, the subscriptions beside them. Once it is here you can ask what the estate looks like in six months, and what the difference costs.',

  'estate.kind.server': 'Server',
  'estate.kind.instance': 'Instance',
  'estate.kind.database': 'Database',
  'estate.kind.saas': 'SaaS',
  'estate.kind.service': 'Service',
  'estate.kind.storage': 'Storage',
  'estate.kind.network': 'Network',
  'estate.kind.endpoint': 'Endpoint',
  'estate.kind.other': 'Other',
  'estate.env.production': 'Production',
  'estate.env.staging': 'Staging',
  'estate.env.development': 'Development',
  'estate.env.shared': 'Shared',
  'estate.status.planned': 'Planned',
  'estate.status.live': 'Live',
  'estate.status.retiring': 'Retiring',
  'estate.status.retired': 'Retired',
  'estate.liveness.live': 'Running',
  'estate.liveness.not_yet': 'Not yet',
  'estate.liveness.gone': 'Gone',
  'estate.liveness.undated': 'No date',
  'estate.vendorKind.cloud': 'Cloud',
  'estate.vendorKind.saas': 'SaaS',
  'estate.vendorKind.hosting': 'Hosting',
  'estate.vendorKind.licence': 'Licences',
  'estate.vendorKind.service': 'Services',
  'estate.vendorKind.other': 'Other',

  'estate.from': 'On',
  'estate.to': 'Compared with',
  'estate.reset': 'Today, six months out',
  'estate.costNow': 'A year, as it stands',
  'estate.costThen': 'A year, as it would be',
  'estate.difference': 'Difference',
  'estate.perYearHint': 'Recurring spend only',
  'estate.componentCount': '{count} components',
  'estate.noChange': 'No change',
  'estate.more': 'more',
  'estate.less': 'less',
  'estate.leaving': 'Gone by then ({count})',
  'estate.arriving': 'Arrived by then ({count})',
  'estate.nothingLeaves': 'Nothing is retired between those two days.',
  'estate.nothingArrives': 'Nothing new arrives between those two days.',
  'estate.stayingCount': '{count} components are in both.',
  'estate.landscapeHint': 'A landscape here is a date rather than a document: both answers come from the dates on the components themselves, so the future never goes stale and nobody has to move anything from one list to another when the day arrives.',
  'estate.undatedCount': '{count} planned without a date, so in neither answer:',

  'estate.move': 'Move',
  'estate.addMove': 'Document a move',
  'estate.movePlaceholder': 'Managed Postgres, off the old cluster',
  'estate.retires': 'Retires',
  'estate.bringsIn': 'Brings in',
  'estate.targetDate': 'By',
  'estate.doneBy': 'Project doing it',
  'estate.noProject': 'No project yet',
  'estate.moveHint': 'Naming what goes and what arrives is what lets the register check the move: it is finished when everything here has actually happened, whatever the status says.',
  'estate.movesHint': 'The way from one landscape to the next. Progress is read from the register rather than from the status — a move claimed done with a server still running is flagged.',
  'estate.moveDone': '{done} of {total} done',
  'estate.moveDisagrees': 'The register disagrees with this status — check what is still running.',
  'estate.noMoves': 'No moves written down',
  'estate.noMovesHint': 'A move is a step from the estate you have to the one you want: what it retires, what it brings in, and by when. Write one down and the register will tell you how far it really got.',
  'estate.deleteMove': 'Delete the move “{name}”?',
  'estate.moveStatus.proposed': 'Proposed',
  'estate.moveStatus.agreed': 'Agreed',
  'estate.moveStatus.in_progress': 'Under way',
  'estate.moveStatus.done': 'Done',
  'estate.moveStatus.abandoned': 'Abandoned',

  'estate.addVendor': 'Add a vendor',
  'estate.website': 'Website',
  'estate.contact': 'Who to ask',
  'estate.contractStart': 'Contract from',
  'estate.contractEnd': 'Contract to',
  'estate.noticeDays': 'Notice, in days',
  'estate.noticeBy': 'Notice has to be given by {date}.',
  'estate.noticeHint': 'The end date and the notice period together give the day you stop being able to leave — which is the one people miss.',
  'estate.noticeByColumn': 'Notice by',
  'estate.noticeDue': '{count} contracts need notice within 90 days:',
  'estate.noVendors': 'No vendors yet',
  'estate.noVendorsHint': 'Who you buy from, what each supplies, and when the contract has to be given notice on. A component can name one, and then the register can total up what each supplier costs.',
  'estate.deleteVendor': 'Delete “{name}”? Its components stay, with no vendor on them.',

  'estate.fromRegister': 'The register says',
  'estate.fromRegisterHint': 'What the components charged to each line come to over this budget’s period. Neither figure is authoritative — one is a plan, the other an inventory — and the useful thing is being told when they disagree.',

  /* ---------------------------------------------------- rates & timesheet */
  'nav.timesheet': 'Timesheet',
  'settings.tabRates': 'Rates',
  'timesheet.title': 'Timesheet',
  'timesheet.tabWeek': 'Week',
  'timesheet.tabCost': 'Cost',
  'timesheet.previous': 'Previous week',
  'timesheet.next': 'Next week',
  'timesheet.thisWeek': 'This week',
  'timesheet.groupBy': 'Group by',
  'timesheet.byPerson': 'By person',
  'timesheet.byProject': 'By project',
  'timesheet.person': 'Person',
  'timesheet.total': 'Total',
  'timesheet.unknown': 'Somebody who has left',
  'timesheet.noProject': 'No project',
  'timesheet.emptyTitle': 'Nothing logged this week',
  'timesheet.emptyHint': 'Time logged on a task shows up here, laid out a day at a time. Use the arrows to look at another week.',
  'timesheet.offTitle': 'Time tracking is switched off',
  'timesheet.offHint': 'An admin can switch it on in Settings → Workspace. Nothing is lost while it is off.',

  'rate.kind.cost': 'What it costs',
  'rate.kind.billable': 'What it is charged at',
  'rate.kindLabel': 'Kind',
  'rate.amount': 'Per hour',
  'rate.perHour': '/h',
  'rate.who': 'Whose hour',
  'rate.where': 'Where',
  'rate.anybody': 'Anybody',
  'rate.everywhere': 'Everywhere',
  'rate.startsOn': 'From',
  'rate.startsOnHint': 'The day this rate takes effect. Hours worked before it keep the rate that was in force then — raising a rate never restates the past.',
  'rate.from': 'from {date}',
  'rate.inForce': 'In force',
  'rate.add': 'Add a rate',
  'rate.none': 'No rates yet. Until there is one, time is counted and nothing is costed.',
  'rate.settingsHint': 'What an hour is worth, from a date. The most specific rate wins: this person on this project, then this person anywhere, then anybody on this project, then the workspace’s own. Changing a rate means adding one that starts on a day — the old row stays, because it is the answer to why last quarter cost what it did.',
  'rate.deleteConfirm': 'Delete this rate? Hours costed at it will be recosted at whatever else applies — possibly at nothing.',
  'rate.cost': 'Cost',
  'rate.revenue': 'Revenue',
  'rate.revenueHint': 'Billable hours only',
  'rate.margin': 'Margin',
  'rate.marginHint': 'Where both sides are known',
  'rate.hours': 'Hours',
  'rate.billable': 'Billable',
  'rate.billableShare': 'Billable share',
  'rate.againstTarget': 'Against target',
  'rate.unrated': '{hours} unrated',
  'rate.unratedHint': 'Hours no rate covers are counted here but not costed. An hour that costs nothing is not a saving — it is a rate nobody has set.',
  'rate.perProject': 'Per project',
  'rate.perPerson': 'Per person',
  'rate.window': 'Window',
  'rate.lastMonths_one': 'Last month',
  'rate.lastMonths_other': 'Last {count} months',
  'rate.target': 'Available hours',
  'rate.targetHint': 'Utilisation against a target needs the hours somebody was available for, which Kolibri does not hold — so it is a number you set, the way the planner’s comfortable load is. Leave it empty and only the billable share is shown.',
  'rate.noTime': 'No time logged yet',
  'rate.noTimeHint': 'Log time on a task and this fills in: what it cost, what it is worth, and how much of it was billable.',
  'rate.adminOnly': 'Rates are visible to owners and admins',
  'rate.adminOnlyHint': 'A rate is close enough to somebody’s pay to keep it to the people who set it — and a total is a rate anybody can divide back out. Hours are not restricted; money is.',

  /* -------------------------------------------------------------- budgets */
  'nav.budgets': 'Budgets',
  'budget.tab': 'Budget',
  'nav.kpis': 'KPIs',
  'nav.decisions': 'Decisions',
  'nav.meetings': 'Meetings',
  'workspace.featureSecrets': 'Secrets',
  'workspace.featureSecretsHint': 'A place to keep API keys, passwords and connection strings, per person and per workspace, instead of pasting them into a page. Switching it on is a promise about where the team’s keys live — turn it on when somebody has decided to move them.',
  'workspace.featureKpi': 'KPIs',
  'workspace.featureKpiHint': 'Numbers the team has undertaken to watch, what they have to reach, and by which milestone.',
  'workspace.featureDecisions': 'Decisions',
  'workspace.featureDecisionsHint': 'A question with options on it: one choice or several, open or by secret ballot, with a deadline and a record of what was settled.',
  'workspace.featureMeetings': 'Meetings',
  'workspace.featureMeetingsHint': 'An agenda everybody can add topics to, notes written together during the meeting, tasks filed from it, and the minutes filed as a page. For members — guests do not see meetings.',

  'kpi.title': 'KPIs',
  'kpi.new': 'New KPI',
  'kpi.untitled': 'Untitled KPI',
  'kpi.gone': 'That KPI is not here',
  'kpi.offTitle': 'KPIs are switched off',
  'kpi.offHint': 'An admin can switch them on under Settings → Workspace.',
  'kpi.emptyTitle': 'Nothing is being watched yet',
  'kpi.emptyHint': 'A KPI is a number somebody has undertaken to move: uptime, churn, lead time. Define it, say which way is better, and record what it reads.',

  'kpi.name': 'What it measures',
  'kpi.description': 'Description',
  'kpi.descriptionPlaceholder': 'What exactly is counted, and what is left out.',
  'kpi.unit': 'Unit',
  'kpi.unitLabel': 'Word after the figure',
  'kpi.unitLabelPlaceholder': 'tickets',
  'kpi.decimals': 'Decimals',
  'kpi.decimalsHint': 'Every value on this KPI is stored at this scale. Changing it later restates every reading, so it is worth setting now.',
  'kpi.directionLabel': 'Which way is better',
  'kpi.cadenceLabel': 'Measured',
  'kpi.cadenceHint': 'What you have undertaken. A reading older than twice this is reported as stale rather than quoted as current.',
  'kpi.baseline': 'Baseline',
  'kpi.baselineHint': 'Where it stood before anybody started. Leave it empty and the first reading is used instead.',
  'kpi.baselineImplied': 'Taken from the first reading',
  'kpi.scope': 'Covers',
  'kpi.wholeWorkspace': 'The whole workspace',

  'kpi.current': 'Now',
  'kpi.trend': 'Change',
  'kpi.trendHint.daily': 'Against yesterday',
  'kpi.trendHint.weekly': 'Against a week ago',
  'kpi.trendHint.monthly': 'Against a month ago',
  'kpi.trendHint.quarterly': 'Against a quarter ago',
  'kpi.target': 'Target',
  'kpi.due': 'By',
  'kpi.pace': 'Pace',
  'kpi.value': 'Value',
  'kpi.note': 'Note',
  'kpi.source': 'Source',
  'kpi.noSource': 'Not stated',
  'kpi.sourcePlaceholder': 'Grafana, the monthly export, a count by hand',
  'kpi.sourceHint': 'Where the number came from. A measurement nobody can trace is one nobody can defend.',
  'kpi.measured': 'Measured',
  'kpi.measuredOn': 'Measured on',
  'kpi.measuredHint': 'Read on {when}',
  'kpi.dueHint': 'Due {when}',
  'kpi.unchanged': 'No change',
  'kpi.milestone': 'Milestone',
  'kpi.noMilestone': 'No milestone',
  'kpi.unknownMilestone': 'A milestone that is gone',
  'kpi.undated': 'No date',
  'kpi.milestoneHint': 'Hang it on a milestone and the deadline moves when that milestone does.',
  'kpi.milestoneNote': 'A target due by a milestone follows that milestone’s date. The promise was “by the time we ship”, not a date on a calendar.',
  'kpi.dueFromMilestone': '{name} is due {when}, and that date is the one in force.',
  'kpi.dueHintPlain': 'Leave it empty for a target with no deadline yet.',

  'kpi.tabOverview': 'Overview',
  'kpi.tabReadings': 'Readings',
  'kpi.tabTargets': 'Targets',
  'kpi.addReading': 'Record a reading',
  'kpi.editReading': 'Edit reading',
  'kpi.recorded': 'Recorded',
  'kpi.deleteReading': 'Delete the reading of {value}?',
  'kpi.noneHere': 'No KPI covers this project',
  'kpi.noneHereHint': 'A KPI scoped to this project, or to the whole workspace, shows up here.',
  'kpi.noReadings': 'Nothing measured yet',
  'kpi.noReadingsHint': 'A KPI with no readings is a promise, not a measure. Record what it reads today.',
  'kpi.addTarget': 'Add a target',
  'kpi.editTarget': 'Edit target',
  'kpi.deleteTarget': 'Delete the target of {value}?',
  'kpi.noTargets': 'Nothing promised yet',
  'kpi.noTargetsHint': 'Several targets are normal — “85 % by June, 90 % by December” is a ladder, and the one in force is the next one ahead.',

  'kpi.chartTitle': 'What it has read',
  'kpi.chartHint': 'Every reading, spaced by when it was taken, against the line from the baseline to the target. The scale does not start at zero — both ends are printed beside it.',
  'kpi.chartCaption': 'Readings of {name} against its target',
  'kpi.paceLabel': '{achieved} % of the way there, against {expected} % expected by now',
  'kpi.paceBackwards': 'It has moved away from the target — {away} % of the distance, in the wrong direction.',
  'kpi.paceSentence': '{achieved} % of the way from the baseline to the target, where a straight line says {expected} % by now.',
  'kpi.staleSince': 'The last reading is {days} days old — more than twice the cadence, so this is not a claim about today.',
  'kpi.summaryTitle': 'Where they stand',
  'kpi.summaryHint': 'Counted under one rule, and the states that are not judgements are counted too — see the guide.',
  'kpi.onMilestone': 'What has to be true by then',
  'kpi.onMilestoneHint': 'A milestone is usually described by what gets built. These are the numbers that have to move with it.',
  'kpi.dangerTitle': 'Delete this KPI',
  'kpi.dangerHint': 'Its readings and targets go with it. Unlike an invoice, a measurement of a metric nobody keeps is not evidence of anything on its own.',
  'kpi.delete': 'Delete KPI',
  'kpi.deleteKpi': 'Delete “{name}” and everything measured against it?',

  'kpi.health.no_data': 'Not measured',
  'kpi.health.no_target': 'No target',
  'kpi.health.stale': 'Stale',
  'kpi.health.on_track': 'On track',
  'kpi.health.at_risk': 'At risk',
  'kpi.health.off_track': 'Off track',
  'kpi.unit.number': 'Number',
  'kpi.unit.percent': 'Percent',
  'kpi.unit.duration': 'Duration',
  'kpi.unit.score': 'Score',
  'kpi.direction.up': 'Higher is better',
  'kpi.direction.down': 'Lower is better',
  'kpi.cadence.daily': 'Daily',
  'kpi.cadence.weekly': 'Weekly',
  'kpi.cadence.monthly': 'Monthly',
  'kpi.cadence.quarterly': 'Quarterly',

  /* ------------------------------------------------------------- products */
  'nav.products': 'Products',
  'workspace.featureProducts': 'Products',
  'workspace.featureProductsHint': 'What the organisation sells: prices, packages, what each product costs to deliver, external contributors, campaigns, break-even and what a customer is worth. Independent of budgets — one is money going out, the other money coming in.',

  'product.title': 'Products',
  'product.new': 'New product',
  'product.edit': 'Edit product',
  'product.archive': 'Archive',
  'product.unarchive': 'Bring back',
  'product.archived': 'Archived',
  'product.unarchived': 'Back in the catalogue',
  'product.showArchived': 'Show {count} archived',
  'product.removeHint': 'Delete “{name}”? Its prices, costs, contributors and its place in any package go with it. It can be restored from the trash.',
  'product.removed': 'Deleted',
  'product.change': 'Change price',
  'product.raisePrice': 'Change the price',
  'product.raisePriceHint': 'Two rows in one step: the current price stops the day before the new one starts. Doing it by hand means remembering to close the old one, and forgetting leaves two prices live at once.',
  'product.wasAmount': 'Was',
  'product.becameAmount': 'Becomes',
  'product.effectiveFrom': 'From',
  'product.raisePreview': '{old} until {until}, then {next} from {from}.',
  'product.raiseBeforeStart': 'This price only starts on {date}. A change has to take effect after that, so the old amount keeps at least one day.',
  'product.raiseAfterEnd': 'This price already ends on {date}. Add a new price rather than changing one that has stopped.',
  'product.priceChanged': 'Price changed',
  'product.changedOn': 'From',
  'product.changeBy': 'Change',
  'product.priceHistory': 'What it used to cost',
  'product.priceHistoryHint': 'Read off the prices themselves. A price is never overwritten — raising one closes a window and opens another, so the old row with its window in the past is the history.',
  'product.priceOverlap': '{count} pair(s) of prices are live at the same time for the same offer. Which one applies is then decided by a rule rather than by you — close the older one.',
  'product.saved': 'Saved',
  'product.gone': 'That product is not here',
  'product.offTitle': 'Products are switched off',
  'product.offHint': 'An admin can switch them on under Settings → Workspace.',
  'product.emptyTitle': 'Nothing is being sold yet',
  'product.emptyHint': 'A product is something the organisation sells: a seminar, a licence, a retainer. Say what it costs to deliver and the margin, the break-even and what a customer is worth follow from it.',
  'product.allArchived': 'Everything here is archived',
  'product.allArchivedHint': 'Nothing is on sale — {count} archived product(s) are still here and can be brought back.',

  'product.tabCatalogue': 'Catalogue',
  'product.tabPackages': 'Packages',
  'product.tabPromotions': 'Campaigns',
  'product.tabScenarios': 'Scenarios',
  'product.tabOverview': 'Overview',
  'product.tabPrices': 'Prices',
  'product.tabCosts': 'Costs',
  'product.tabPackage': 'Package',
  'product.tabSimulation': 'Simulation',

  'product.name': 'Name',
  'product.code': 'Code',
  'product.codePlaceholder': 'SEM-PM-2',
  'product.description': 'Description',
  'product.group': 'Group',
  'product.groups': 'Product groups',
  'product.groupName': 'New group',
  'product.groupsHint': 'A family for the reports that ask about a line of business. Deleting one leaves its products behind, ungrouped.',
  'product.ungrouped': 'Ungrouped',
  'product.removeGroup': 'Remove {name}',
  'product.removeGroupHint': 'Remove the group “{name}”? Its products stay, ungrouped.',
  'product.currency': 'Currency',
  'product.statusLabel': 'Status',
  'product.kindLabel': 'What it is',
  'product.kindSingle': 'Single product',
  'product.kindBundle': 'Package',
  'product.kindSingleHint': 'Sold on its own.',
  'product.kindBundleHint': 'Contains other products. You choose them on the Package tab once this is saved.',
  'product.kindLocked': 'It already contains {count} product(s), so it stays a package. Take them out on the Package tab first.',
  'product.healthLabel': 'Standing',
  'product.package': 'Package',
  'product.category': 'Category',
  'product.vendor': 'Vendor',
  'product.note': 'Note',
  'product.amount': 'Amount',
  'product.month': 'Month',
  'product.months': 'Months',
  'product.units': 'Units',
  'product.quantity': 'Quantity',
  'product.window': 'Window',

  'product.countLabel': 'Products',
  'product.contributionLabel': 'Contribution, one of each',
  'product.contributionHint': 'What one unit of every product would earn. Not revenue and not a forecast — for that, run a simulation.',
  'product.monthlyCostLabel': 'Fixed, per month',
  'product.monthlyCostHint': 'Costs incurred every month whatever is sold.',
  'product.unpricedLabel': 'Unpriced',
  'product.unpricedHint': 'Products nobody has put a price on. They are left out of every figure above.',
  'product.horizonNote': 'Break-even is over {months} months with one delivery. A product page can be read over any horizon.',

  'product.price': 'Price',
  'product.unitCost': 'Cost per unit',
  'product.margin': 'Margin',
  'product.marginHint': '{percent}% of the price',
  'product.marginTotal': 'Margin',
  'product.contribution': 'Contribution',
  'product.atALoss': 'at a loss',
  'product.unpriced': 'Not priced',
  'product.unitCostHint': 'Only what varies with each unit sold.',
  'product.unitPicture': 'Where one unit goes',
  'product.unitPictureHint': 'The bar is the price; the darker part is what delivering one unit costs. A cost that runs past the end of the bar is a unit sold at a loss.',
  'product.perUnit': 'per {unit}',

  'product.breakEven': 'Break-even',
  'product.breakEvenUnits': '{units} units',
  'product.breakEvenSentence_one': '{units} unit over {months} months covers the {fixed} of fixed cost.',
  'product.breakEvenSentence_other': '{units} units over {months} months cover the {fixed} of fixed cost.',
  'product.breakEvenMonth': 'Breaks even',
  'product.neverBreaksEven': 'Not within the horizon',
  'product.blocked.no_price': 'Nobody has priced it',
  'product.blocked.no_contribution': 'Every unit loses money — no volume fixes this',
  'product.blocked.over_capacity': 'More units than a delivery can take',

  'product.scope': 'Scope',
  'product.scopeHint': 'How much one unit contains, and of what. This is the number products are compared by, so it is two fields rather than a sentence.',
  'product.scopeAmount': 'How much',
  'product.scopeUnit': 'Of what',
  'product.scopeUnitPlaceholder': 'days, months, hours',
  'product.unitLabel': 'One unit is a',
  'product.unitPlaceholder': 'seat, licence, day',
  'product.capacity': 'Capacity',
  'product.capacityNone': 'no ceiling',
  'product.capacityHint': '{capacity} per delivery',

  'product.capabilities': 'Capabilities',
  'product.capabilitiesHint': 'Named once for the whole workspace, so two products claiming the same one say it the same way.',
  'product.newCapability': 'New capability',
  'product.noCapabilities': 'None recorded',
  'product.unknownCapability': 'Unknown capability',
  'product.tabCapabilities': 'Capabilities',
  'product.capabilitiesTabHint': 'The workspace’s vocabulary. A package inherits its parts’ capabilities — there is nothing to tick there.',
  'product.addCapability': 'Add a capability',
  'product.editCapabilityTitle': 'Edit capability',
  'product.claimedBy': 'Claimed by',
  'product.inheritedBy': 'Inherited by',
  'product.nobodyClaims': 'No product',
  'product.removeCapabilityHint': 'Remove this capability from the workspace? Every product claiming it stops showing it.',
  'product.capabilityTaken': 'That name is already in use.',
  'product.noCapabilitiesHint': 'Name a feature once, then tick it on every product that has it.',
  'product.includedCapabilities': 'What the customer gets',
  'product.includedCapabilitiesHint': 'The package’s own capabilities plus every part’s. Nothing is typed twice.',

  'product.contributors': 'External contributors',
  'product.contributorsHint': 'Speakers, trainers and subcontractors from outside. Their fee counts as a cost of the basis it is on, so it is in the margin already.',
  'product.addContributor': 'Add a contributor',
  'product.editContributor': 'Edit contributor',
  'product.contributorSaved': 'Contributor saved',
  'product.removeContributorHint': 'Remove this contributor? Their fee stops counting against the product.',
  'product.role': 'Role',
  'product.rolePlaceholder': 'Speaker, trainer, co-author',
  'product.organisation': 'Organisation',
  'product.email': 'Email',
  'product.fee': 'Fee',

  'product.pricesHint': 'A product may have several prices at once. Which one applies is worked out from the order size and the day.',
  'product.addPrice': 'Add a price',
  'product.editPrice': 'Edit price',
  'product.priceSaved': 'Price saved',
  'product.removePriceHint': 'Remove this price?',
  'product.noPrices': 'No price yet',
  'product.noPricesHint': 'Until there is one, every margin and break-even on this product reads as “not priced” rather than as zero.',
  'product.priceName': 'What it is called',
  'product.priceNamePlaceholder': 'List, early bird, ten or more',
  'product.unnamedPrice': 'Unnamed',
  'product.priceKindLabel': 'Kind',
  'product.priceKindHint': 'An internal transfer price never counts as revenue.',
  'product.minQuantity': 'From',
  'product.minQuantityHint': 'The smallest order this price applies to.',
  'product.billingLabel': 'Charged',
  'product.billingMovedHint': 'How often the customer is charged is a property of the price, not of the product — the same product can be sold monthly, yearly and two-yearly at once. Set it per price under Prices.',
  'product.scopeUnitNone': 'Not stated',
  'product.capacityApplies': 'This has a capacity per delivery',
  'product.capacityAppliesHint': 'A seminar with twelve seats. Leave it off for anything not delivered — a licence with a capacity of 1 forecasts one sale a month.',
  'product.scopeUnit.days': 'Days',
  'product.scopeUnit.months': 'Months',
  'product.scopeUnit.hours': 'Hours',
  'product.scopeUnit.years': 'Years',
  'product.scopeUnit.weeks': 'Weeks',
  'product.scopeUnit.gigabytes': 'Gigabytes',
  'product.unitOption.seat': 'Seat',
  'product.unitOption.licence': 'Licence',
  'product.unitOption.day': 'Day',
  'product.unitOption.booking': 'Booking',
  'product.unitOption.device': 'Device',
  'product.unitOption.user': 'User',
  'product.periods': 'Sold',
  'product.mixedPeriods': 'Sold both as a sale and as a subscription — the two have no common figure.',
  'product.cappedByTerm': 'The churn says {churn} months, the minimum term says {term}. Inside the term a customer cannot leave, so the term is the figure.',
  'product.validFrom': 'Valid from',
  'product.validTo': 'Valid to',
  'product.appliesNow': 'Applies today',

  'product.costsHint': 'What varies with what is the only distinction a break-even is made of. External contributors’ fees are counted in the totals below.',
  'product.addCost': 'Add a cost',
  'product.editCost': 'Edit cost',
  'product.costSaved': 'Cost saved',
  'product.removeCostHint': 'Remove this cost?',
  'product.noCosts': 'No costs recorded',
  'product.noCostsHint': 'A product with no costs shows a margin of 100%, which is nobody’s product.',
  'product.basisLabel': 'Varies with',
  'product.byCategory': 'By category',
  'product.byCategoryHint': 'The same vocabulary a budget line uses, so the two can be read side by side.',

  'product.notAPackage': 'This is not a package',
  'product.notAPackageHint': 'A package is a product that contains other products. It keeps its own price, and what the parts are worth separately is computed — so the discount is visible rather than implied.',
  'product.makePackage': 'Make it a package',
  'product.part': 'Part',
  'product.addPart': 'Add a product',
  'product.removePartHint': 'Take this out of the package?',
  'product.unknownPart': 'Unknown product',
  'product.listValue': 'Parts separately',
  'product.listValueHint': 'Each part at its own price, times how many.',
  'product.packagePrice': 'Package price',
  'product.saving': 'Saving',
  'product.premium': 'Premium',
  'product.unpricedShort': '{count} unpriced',
  'product.periodsHeading': 'By billing period',
  'product.periodsHint': 'A one-off and a monthly fee have no common figure, so each period is compared on its own. The figures above are for the period the package is quoted in.',
  'product.periodNotCharged': 'Not charged',
  'product.unpricedParts': '{count} parts have no price, so they are missing from the figure above.',

  'product.retention': 'Retention',
  'product.retentionHint': 'How long a customer stays and what winning them costs. Everything here is an assumption — nothing in Kolibri counts customers.',
  'product.retentionOneOff': 'Sold once, so there is nothing to retain. Switch the renewal on if it is a subscription.',
  'product.renewalLabel': 'At the end of the term',
  'product.termMonths': 'Minimum term, months',
  'product.priceTerm': 'Commitment',
  'product.priceTermHint': 'Months the customer is tied in for this price. Empty means the product’s own, {term}.',
  'product.termFree': 'no commitment',
  'product.priceSpan': '{low} – {high}',
  'product.termNone': 'none',
  'product.termMonthsShort': '{months} mo.',
  'product.churn': 'Churn, % a month',
  'product.churnFieldHint': 'Customers lost each month. Typed in from wherever they are counted.',
  'product.churnHint': '{percent}% a month',
  'product.churnOwn': 'the product’s own',
  'product.churnOverride': 'Churn for this scenario, % a month',
  'product.noChurn': 'No churn recorded, so there is no lifetime to compute.',
  'product.expectedMonths': 'Stays for',
  'product.lifetimeValue': 'Lifetime value',
  'product.lifetimeValueHint': 'Contribution over that life, after what winning them cost.',
  'product.acquisitionCost': 'Acquisition cost',
  'product.acquisitionHint': 'Paid once, before any revenue — which is why it is not a per-unit cost.',
  'product.payback': 'Payback',
  'product.paybackMonths': '{months} months',
  'product.paybackHint': 'Until the {cost} is back.',
  'product.retained': 'Still there',
  'product.retentionCaption': 'How much of one month’s customers is left, month by month. Geometric decay from one churn figure — real churn is heavier early and lighter later.',
  'product.retentionSentence': 'A customer stays about {months} months and is worth {value} over that.',

  'product.newPromotion': 'New campaign',
  'product.editPromotion': 'Edit campaign',
  'product.promotionSaved': 'Campaign saved',
  'product.removePromotionHint': 'Remove this campaign?',
  'product.noPromotions': 'No campaigns',
  'product.noPromotionsHint': 'A campaign is a price that is different for a while, what it applies to, what running it costs and what it is expected to sell.',
  'product.noPromotionsYet': 'No campaigns to apply yet.',
  'product.promoKindLabel': 'Takes off',
  'product.promoValue': 'Value',
  'product.promoStacked': '{count} pair(s) of campaigns will both be applied to the same product at once, so their discounts multiply rather than add. Affected: {products}.',
  'product.promoDetailHint': 'What this campaign does to every product it covers. The discounted price is this campaign alone — where two overlap, a customer pays less still.',
  'product.promoReachesNothing': 'This campaign covers no product that is on sale. Its products may have been archived, or its group may be empty.',
  'product.product': 'Product',
  'product.listPrice': 'Without it',
  'product.withPromo': 'With it',
  'product.givenAway': 'Given away',
  'product.paysFrom': 'Pays for itself',
  'product.paysAlways': 'at any volume — nothing was spent on it',
  'product.paysAbove': 'from {units} a month that would have sold anyway',
  'product.paysNoUplift': 'never, while no extra sales are expected of it',
  'product.paysNeverSmall': 'never — the expected extra sales earn less than the discount gives away',
  'product.paysNoPrice': 'not answerable — the product has no price',
  'product.promoAssumption': 'Figured against the {uplift}% more this campaign is expected to sell and the {spend} spent on running it. The uplift is a guess somebody wrote down, not a measurement.',
  'product.appliesTo': 'Applies to',
  'product.appliesToHint': 'Pick nothing and it covers the whole catalogue.',
  'product.wholeCatalogue': 'The whole catalogue',
  'product.spend': 'Costs to run',
  'product.uplift': 'Expected uplift, %',
  'product.upliftHint': 'A guess, written down so it can be compared with what happened.',
  'product.phaseLabel': 'Phase',
  'product.statusHint': 'Nothing applies a campaign to a price until a simulation names it.',
  'product.startsOn': 'Starts',
  'product.endsOn': 'Ends',

  'product.scenariosHint': 'A scenario is a set of assumptions kept beside the catalogue rather than instead of it. Nothing it holds edits a price or a cost.',
  'product.newScenario': 'New scenario',
  'product.editScenario': 'Edit scenario',
  'product.scenarioSaved': 'Scenario saved',
  'product.saveScenario': 'Save as a scenario',
  'product.removeScenarioHint': 'Remove this scenario?',
  'product.noScenarios': 'No scenarios',
  'product.noScenariosHint': 'Something to show a steering committee on Tuesday and throw away on Wednesday.',
  'product.scenarioProduct': 'About',
  'product.simulationHint': 'Every figure below is computed here and now from the prices and costs on this product. Nothing is written.',
  'product.unitsFirstMonth': 'Units, first month',
  'product.deliveries': 'Deliveries a month',
  'product.growth': 'Growth, % a month',
  'product.priceChange': 'Price change, %',
  'product.costChange': 'Cost change, %',
  'product.campaignsApplied': 'Campaigns applied',
  'product.revenue': 'Revenue',
  'product.cost': 'Cost',
  'product.cumulative': 'Cumulative',
  'product.overMonths': 'over {months} months',
  'product.turnedAway': '{units} units could not be delivered — capacity was reached.',
  'product.priceAfter': 'List {list}, and {price} after the campaigns.',
  'product.projectionCaption': 'Cumulative margin for {name}, month by month. The line crosses zero where it starts paying for itself.',

  'product.status.draft': 'Draft',
  'product.status.active': 'Active',
  'product.status.retired': 'Retired',
  'product.health.loss': 'At a loss',
  'product.health.thin': 'Thin',
  'product.health.unpriced': 'Unpriced',
  'product.health.no_costs': 'Uncosted',
  'product.health.healthy': 'Healthy',
  'product.basis.period': 'Every month',
  'product.basis.delivery': 'Every delivery',
  'product.basis.unit': 'Every unit',
  'product.basisHint.period': 'Incurred every month however many customers there are: a platform licence, the tooling, a retainer. A cost that grows with each customer belongs under Every unit, even when it is billed monthly.',
  'product.basisHint.delivery': 'Incurred each time it is delivered, whoever attends: the room, the catering, a speaker’s day fee.',
  'product.basisHint.unit': 'Incurred per unit sold, and the only one that comes off the price. On a subscription it recurs: every month, for every active customer, alongside the price — so €1 a month per customer belongs here.',
  'product.priceKind.list': 'List',
  'product.priceKind.volume': 'Volume',
  'product.priceKind.partner': 'Partner',
  'product.priceKind.internal': 'Internal',
  'product.billing.once': 'Once',
  'product.billing.monthly': 'Monthly',
  'product.billing.quarterly': 'Quarterly',
  'product.billing.yearly': 'Yearly',
  'product.renewal.none': 'It ends',
  'product.renewal.manual': 'Renewed by hand',
  'product.renewal.auto': 'Renews itself',
  'product.promoKind.percent': 'A percentage',
  'product.promoKind.amount': 'An amount',
  'product.promoKind.price': 'A fixed price',
  'product.phase.draft': 'Draft',
  'product.phase.live': 'Live',
  'product.phase.cancelled': 'Cancelled',
  'product.phase.upcoming': 'Upcoming',
  'product.phase.running': 'Running',
  'product.phase.ended': 'Ended',

  /* ------------------------------------------------------------- decisions */
  'decision.title': 'Decisions',
  'decision.new': 'New decision',
  'decision.untitled': 'Untitled decision',
  'decision.gone': 'That decision is not here',
  'decision.offTitle': 'Decisions are switched off',
  'decision.offHint': 'An admin can switch them on under Settings → Workspace.',
  'decision.emptyTitle': 'Nothing is being decided yet',
  'decision.emptyHint': 'A decision is a question with options on it: which of these three, by Friday, and who said what. The result stays here after it closes.',
  'decision.question': 'The question',
  'decision.questionPlaceholder': 'Which of these do we build first?',
  'decision.description': 'Context',
  'decision.modeLabel': 'How many may be picked',
  'decision.modeHint': 'Picking a second option in a single-choice vote takes back the first.',
  'decision.mode.single': 'One option',
  'decision.mode.multiple': 'Several options',
  'decision.visibilityLabel': 'Who can see how people voted',
  'decision.visibilityHint': 'A secret ballot sends nobody the votes but their own, so the count comes from the server rather than from this device.',
  'decision.visibility.open': 'Everybody',
  'decision.visibility.anonymous': 'Nobody — secret ballot',
  'decision.lockedHint': 'Fixed now that somebody has voted. Changing it would invalidate the votes already cast.',
  'decision.closesAt': 'Closes',
  'decision.closesAtHint': 'Optional. After this it stops taking votes and only shows the result.',
  'decision.options': 'Options',
  'decision.optionsHint': 'At least two. A question with nothing to pick between is not a decision.',
  'decision.optionPlaceholder': 'Option {n}',
  'decision.addOption': 'Add an option',
  'decision.removeOptionConfirm': 'Remove “{label}” and the votes for it?',
  'decision.deleteConfirm': 'Delete this decision, its options and every vote on it?',
  'decision.open': 'Open',
  'decision.until': 'Until {when}',
  'decision.closed': 'Closed',
  'decision.expired': 'The deadline has passed',
  'decision.close': 'Close it',
  'decision.reopen': 'Reopen it',
  'decision.closedHint': 'This vote is closed.',
  'decision.noOptions': 'Nothing to pick between yet.',
  'decision.onTask': 'Being decided',
  'decision.turnout_one': '1 person has voted',
  'decision.turnout_other': '{count} people have voted',
  'decision.countedByServer': 'counted by the server, because the ballot is secret',

  /* -------------------------------------------------------------- meetings */
  'meeting.title': 'Meetings',
  'meeting.new': 'New meeting',
  'meeting.untitled': 'Meeting',
  'meeting.gone': 'That meeting is not here',
  'meeting.offTitle': 'Meetings are switched off',
  'meeting.offHint': 'An admin can switch them on under Settings → Workspace.',
  'meeting.emptyTitle': 'No meetings yet',
  'meeting.emptyHint': 'A meeting has a time, the people expected and an agenda everybody can add topics to. The notes are written together, and what comes out of it becomes tasks.',
  'meeting.upcoming': 'Still ahead',
  'meeting.closedList': 'Closed',
  'meeting.name': 'Title',
  'meeting.namePlaceholder': 'Weekly sync',
  'meeting.when': 'Date and time',
  'meeting.where': 'Place',
  'meeting.wherePlaceholder': 'A room, or the link to a call',
  'meeting.purpose': 'What it is for',
  'meeting.attendees': 'Attendees',
  'meeting.addPerson': 'Add a person',
  'meeting.removePerson': 'Remove {name}',
  'meeting.assignees': 'Responsible',
  'meeting.addAssignee': 'Make someone responsible',
  'meeting.noTime': 'No time set',
  'meeting.planned': 'Planned',
  'meeting.done': 'Closed',
  'meeting.unclosed': 'Nobody closed it',
  'meeting.close': 'Close the meeting',
  'meeting.reopen': 'Reopen',
  'meeting.points_one': '1 topic',
  'meeting.points_other': '{count} topics',
  'meeting.openPoints_one': '1 open',
  'meeting.openPoints_other': '{count} open',
  'meeting.agenda': 'Agenda',
  'meeting.agendaEmpty': 'Nothing on the agenda yet. Anybody in the workspace can add a topic.',
  'meeting.addTopic': 'Add a topic',
  'meeting.topicPlaceholder': 'What should be talked about?',
  'meeting.topicDescription': 'Description (optional)',
  'meeting.addDescription': 'Add a description',
  'meeting.topicTitle': 'Topic {n}',
  'meeting.topicMenu': 'More for this topic',
  'meeting.projects': 'Projects',
  'meeting.addProject': 'Add a project',
  'meeting.removeProject': 'Remove {name}',
  'meeting.status.open': 'Open',
  'meeting.status.discussed': 'Discussed',
  'meeting.status.deferred': 'Deferred',
  'meeting.statusLabel': 'Where this topic got to',
  'meeting.broughtBy': 'Brought by {name}',
  'meeting.continued': 'Continued from {when}',
  'meeting.notes': 'Notes',
  'meeting.notesPlaceholder': 'What was said and agreed. Everybody in the meeting can write here at the same time.',
  'meeting.writing': '{name} is writing here…',
  'meeting.writingTwo': '{first} and {second} are writing here…',
  'meeting.writingMany': 'Several people are writing here…',
  'meeting.tasks': 'Tasks',
  'meeting.newTask': 'File a task',
  'meeting.taskTitle': 'Task',
  'meeting.taskProject': 'Project',
  'meeting.taskAssignee': 'Assignee',
  'meeting.nobody': 'Nobody yet',
  'meeting.taskCreated': 'Filed “{title}”',
  'meeting.moveUp': 'Move up',
  'meeting.moveDown': 'Move down',
  'meeting.deleteTopic': 'Remove the topic',
  'meeting.deleteTopicConfirm': 'Remove “{title}” from the agenda, with its notes? Tasks filed from it stay.',
  'meeting.deleteConfirm': 'Delete this meeting and its whole agenda? It can be restored from the trash.',
  'meeting.details': 'Details',
  'meeting.carry': 'Carry open topics forward',
  'meeting.carryTitle': 'Carry open topics forward',
  'meeting.carryNone': 'Every topic was discussed, so there is nothing to carry forward.',
  'meeting.carryInto_one': 'The 1 topic that was not discussed goes onto the agenda of “{meeting}”, {when}. Its notes stay here.',
  'meeting.carryInto_other': 'The {count} topics that were not discussed go onto the agenda of “{meeting}”, {when}. Their notes stay here.',
  'meeting.carryNew_one': 'There is no later meeting yet, so a follow-up is called for {when}, with the same people, and the 1 topic that was not discussed goes onto its agenda.',
  'meeting.carryNew_other': 'There is no later meeting yet, so a follow-up is called for {when}, with the same people, and the {count} topics that were not discussed go onto its agenda.',
  'meeting.carryGo': 'Carry them forward',
  'meeting.carried_one': '1 topic carried forward',
  'meeting.carried_other': '{count} topics carried forward',
  'meeting.minutes': 'File the minutes as a page',
  'meeting.minutesAgain': 'Rewrite the minutes',
  'meeting.minutesOpen': 'Open the minutes',
  'meeting.minutesFiled': 'The minutes are filed as a page',
  'meeting.minutesConfirm': 'The minutes page is rewritten from the meeting as it stands now. The previous text stays in the page history.',
  'meeting.minutesRewrite': 'Rewrite',
  'meeting.minutesTitle': 'Minutes: {title}',
  'meeting.minutesBroughtBy': 'Brought by',
  'meeting.minutesContinued': 'continued from an earlier meeting',
  'meeting.minutesEmpty': 'Nothing was on the agenda.',
  'meeting.fromMeeting': 'From the meeting',

  'budget.title': 'Budgets',
  'budget.budget': 'Budget',
  'budget.new': 'New budget',
  'budget.name': 'Name',
  'budget.namePlaceholder': 'Platform 2026',
  'budget.description': 'What it covers',
  'budget.currency': 'Currency',
  'budget.currencyHint': 'One per budget. Nothing here converts between two — a rate is a fact about a day, and budgets in different currencies are shown as separate totals rather than added.',
  'budget.approved': 'Approved',
  'budget.approvedHint': 'The signed-off total. Leave it empty until somebody has actually approved one — the plan is a figure of its own.',
  'budget.period': 'Period',
  'budget.periodStart': 'From',
  'budget.periodEnd': 'To',
  'budget.periodEndHint': 'Leave empty for twelve months from the start.',
  'budget.status': 'Status',
  'budget.owner': 'Owner',
  'budget.scope': 'Who it belongs to',
  'budget.scopeWorkspace': 'The whole workspace',
  'budget.scopeHint': 'Who can see it. Not the same as who pays for it — that is the split on each line, and a workspace-wide budget can still charge most of itself to one project.',
  'budget.status.draft': 'Draft',
  'budget.status.active': 'Active',
  'budget.status.closed': 'Closed',
  'budget.archived': 'Archived',
  'budget.month': 'Month',
  'budget.planned': 'Planned',
  'budget.actualOfPlanned': '{actual} of {planned}',
  'budget.actual': 'Actual',
  'budget.forecast': 'Forecast',
  'budget.variance': 'Variance',
  'budget.remaining': 'Still to come',
  'budget.committed': 'Committed',
  'budget.invoiced': 'Invoiced',
  'budget.paid': 'Paid',
  'budget.spent': 'Spent',
  'budget.unplanned': 'Unplanned',
  'budget.over': 'over',
  'budget.under': 'under',
  'budget.health.unset': 'No envelope',
  'budget.health.healthy': 'On track',
  'budget.health.tight': 'Tight',
  'budget.health.over': 'Over',

  'budget.category.infrastructure': 'Infrastructure',
  'budget.category.investment': 'Investment',
  'budget.category.people': 'People',
  'budget.category.licences': 'Licences',
  'budget.category.services': 'Services',
  'budget.category.travel': 'Travel',
  'budget.category.training': 'Training',
  'budget.category.contingency': 'Contingency',
  'budget.category.other': 'Other',
  'budget.kind.opex': 'Running cost',
  'budget.kind.capex': 'Investment',
  'budget.recurrence.once': 'One-off',
  'budget.recurrence.monthly': 'Monthly',
  'budget.recurrence.quarterly': 'Quarterly',
  'budget.recurrence.yearly': 'Yearly',
  'budget.confidence.committed': 'Committed',
  'budget.confidence.likely': 'Likely',
  'budget.confidence.possible': 'Possible',
  'budget.stage.committed': 'Committed',
  'budget.stage.invoiced': 'Invoiced',
  'budget.stage.paid': 'Paid',

  'budget.allocProject': 'Project',
  'budget.allocShare': 'Share, percent',
  'budget.allocAdd': 'Charge another project',
  'budget.allocNone': 'Not charged to any project yet.',
  'budget.allocExact': 'Adds up to 100%.',
  'budget.allocOff': 'Adds up to {percent}% — it will be scaled to 100% when saved.',
  'budget.unallocated': 'Unallocated',
  'budget.unknownProject': 'Unknown project',

  'budget.tabPlan': 'Plan',
  'budget.tabActuals': 'Actuals',
  'budget.tabScenarios': 'Scenarios',
  'budget.tabDashboard': 'Dashboard',
  'budget.line': 'Cost',
  'budget.lineName': 'What it is',
  'budget.lineNamePlaceholder': 'Kubernetes cluster',
  'budget.amount': 'Amount',
  'budget.amountEach': 'Each',
  'budget.amountHint': 'Per occurrence. Twelve months of hosting is one monthly line, not twelve rows.',
  'budget.total': 'Over the period',
  'budget.category': 'Category',
  'budget.kind': 'Kind',
  'budget.recurrence': 'How often',
  'budget.vendor': 'Supplier',
  'budget.confidence': 'Confidence',
  'budget.charges': 'Charged to',
  'budget.addLine': 'Plan a cost',
  'budget.noLines': 'Nothing planned yet',
  'budget.noLinesHint': 'A budget is the sum of its lines. Add the first — infrastructure, an investment, a supplier — and the plan, the forecast and the charts follow from it.',
  'budget.deleteLine': 'Delete “{name}”? Invoices filed against it stay, as unplanned spend.',

  'budget.addActual': 'Record spend',
  'budget.confirmTitle': 'Take the plan across',
  'budget.confirmOne': 'Confirm',
  'budget.confirmAll_one': 'Confirm {count} · {amount}',
  'budget.confirmAll_other': 'Confirm all {count} · {amount}',
  'budget.confirmAlready': '{amount} recorded',
  'budget.confirmSettled': 'Everything planned for {month} is recorded.',
  'budget.confirmOutside': '{month} is outside this budget’s period ({from} – {to}). Give it an end date in Settings to plan further.',
  'budget.confirmNothing': 'Nothing is planned for {month}.',
  'budget.confirmHint': 'Each one becomes an ordinary record you can edit or delete.',
  'budget.confirmed_one': 'Recorded',
  'budget.confirmed_other': '{count} recorded',
  'budget.actualDescription': 'What it was for',
  'budget.actualDescriptionPlaceholder': 'AWS, January',
  'budget.spentOn': 'Date',
  'budget.stage': 'Stage',
  'budget.stageHint': 'Committed is a purchase order nobody has invoiced yet. Leaving it out of the total is how a budget looks healthy right up until the invoices land.',
  'budget.reference': 'Invoice / PO',
  'budget.against': 'Against',
  'budget.noLine': 'Nothing planned',
  'budget.noActuals': 'No spend recorded',
  'budget.noActualsHint': 'Record what has actually gone — paid, invoiced, or committed — and the plan gets something to be compared against.',
  'budget.deleteActual': 'Delete this record of {amount}?',

  'budget.scenario': 'Scenario',
  'budget.scenarios': 'Scenarios',
  'budget.addScenario': 'New scenario',
  'budget.scenarioName': 'Name',
  'budget.scenarioNamePlaceholder': 'Migration slips a quarter',
  'budget.scenarioPlan': 'The plan as written',
  'budget.scenarioHint': 'A scenario never edits the plan. It is a list of changes applied on the way to a total, so you can show one on Tuesday and throw it away on Wednesday.',
  'budget.noScenarios': 'No scenarios yet',
  'budget.noScenariosHint': 'A scenario answers “what if”. Cut a category by a third, slip an investment by a quarter, or carry only the money somebody has actually signed for — without touching the plan the team is working to.',
  'budget.adjustment': 'Change',
  'budget.addAdjustment': 'Add a change',
  'budget.adjApplies': 'Applies to',
  'budget.adjEveryLine': 'Every cost',
  'budget.adjFactor': 'Scale to',
  'budget.adjDelta': 'Add',
  'budget.adjShift': 'Shift by months',
  'budget.adjDrop': 'Drop it',
  'budget.adjNote': 'Why',
  'budget.weights': 'Carry unsigned money at',
  'budget.weightsHint': 'How much of each confidence level this scenario counts. The honest version of what every finance spreadsheet does by hand.',
  'budget.scenarioDelta': '{amount} against the plan',
  'budget.deleteScenario': 'Delete the scenario “{name}”?',
  'budget.apply': 'Show this scenario',
  'budget.applied': 'Showing “{name}” instead of the plan',
  'budget.clearScenario': 'Back to the plan',

  'budget.burn': 'Where the money goes',
  'budget.burnHint': 'Running totals across the period. The plan is what was written down, the actual is what has gone, and the dashed line is where that ends up — closed months as they happened, the rest at whichever is larger, the plan or what has already gone.',
  'budget.burnCaption': 'Planned, actual and forecast spend over {period}',
  'budget.monthly': 'Month by month',
  'budget.monthlyHint': 'Not cumulative. A month where nothing was planned and something went is the one worth explaining.',
  'budget.monthlyCaption': 'Planned against actual spend per month',
  'budget.byCategory': 'By category',
  'budget.byCategoryHint': 'What sort of thing this budget is made of, and how much of each has gone.',
  'budget.project': 'Project',
  'budget.byProject': 'By project',
  'budget.byProjectCaption': 'Planned against actual spend per project',
  'budget.byCategoryCaption': 'Planned against actual spend per category',
  'budget.byConfidenceCaption': 'What the plan is worth at each level of confidence',
  'budget.byKindCaption': 'Running costs against money spent to build',
  'budget.byProjectHint': 'What each project is being charged, out of every line that names it. A cost split across two projects appears in both, and the halves add up to the whole.',
  'budget.byConfidence': 'How sure the plan is',
  'budget.byKind': 'Running against building',
  'budget.usedOfApproved': '{used} of {envelope}',
  'budget.elapsed': '{percent}% of the period gone',
  'budget.runRate': 'At this rate',
  'budget.runRateAside': 'A second opinion on the forecast, worth attention when the two disagree.',
  'budget.runRateHint': 'If spending carries on as it has',
  'budget.forecastHint': 'Closed months as they went, the plan for the rest',
  'budget.varianceHint': 'Against the approved total, or against the plan when nothing has been approved.',
  'budget.unplannedHint': 'No plan line accounts for it',

  'budget.emptyTitle': 'No budgets yet',
  'budget.emptyHint': 'A budget is an envelope of money over a period: what you expect to spend, what has actually gone, and what that means for the end of the year. Infrastructure, investments, suppliers — plan them once and every project they touch gets its share.',
  'budget.offTitle': 'Budgets are switched off',
  'budget.offHint': 'An admin can switch them on in Settings → Workspace. Nothing is lost while they are off — the figures stay where they were.',
  'budget.projectNone': 'Nothing in this workspace charges anything to this project yet. A budget line charges a project through its split.',
  'budget.projectPlanned': 'Planned for this project',
  'budget.projectActualHint_one': '{amount} gone, from one budget',
  'budget.projectActualHint_other': '{amount} gone, across {count} budgets',
  'budget.projectHint': 'One project’s share of every budget that charges it. A cost split between projects appears here at its share, never in full.',
  'budget.plannedShare': 'Its share of the plan',
  'budget.actualShare': 'Its share of the spend',
  'budget.deletedToast': 'Budget deleted',
  'budget.deleteBudget': 'Delete the budget “{name}”? Its plan, its records of spend and its scenarios go with it.',
  'budget.count_one': '{count} budget',
  'budget.count_other': '{count} budgets',
  'budget.portfolioTitle': 'Across every budget',
  'budget.portfolioHint': 'Totals per currency, because nothing here invents an exchange rate.',

  'foreign.title': 'This looks like a {tool} export',
  'foreign.summary': '“{name}” — {count} issues, read from a {tool} export.',
  'foreign.unnamed': 'Untitled',
  'foreign.leftBehind': 'What is not coming across',
  'foreign.import': 'Import it anyway',
  'foreign.caveat': 'These converters were written against each tool’s documented shape and have never been run against a real export from a real instance. Nothing is overwritten — the import always makes a new project — so the safe way to find out is to try it and look.',
  'import.title': 'Import from a file',
  'import.chooseFile': 'Choose a CSV file',
  'import.chooseHint': 'Exported from Jira, Plane, OpenProject, a spreadsheet — up to {max} rows. Nothing is written until you have seen what it read.',
  'import.otherFile': 'Another file',
  'import.readRows_one': '{count} row',
  'import.readRows_other': '{count} rows',
  'import.delimiterComma': 'separated by commas',
  'import.delimiterSemicolon': 'separated by semicolons',
  'import.delimiterTab': 'separated by tabs',
  'import.delimiterPipe': 'separated by pipes',
  'import.mapHint': 'What each column means. The guesses come from the header names — change any that are wrong, and leave the rest ignored.',
  'import.mapColumn': 'What the column “{column}” means',
  'import.ignore': 'Ignore',
  'import.needTitle': 'One column has to be the title. A task without one is nothing.',
  'import.preview': 'Read it through',
  'import.confirm_one': 'Import {count} task',
  'import.confirm_other': 'Import {count} tasks',
  'import.confirmHint': 'Nothing has been written yet. Importing creates these as ordinary tasks — you can select them afterwards and change or delete them together.',
  'import.willCreate_one': '{count} task',
  'import.willCreate_other': '{count} tasks',
  'import.willSkip_one': '{count} row skipped',
  'import.willSkip_other': '{count} rows skipped',
  'import.problemCount_one': '{count} thing to look at',
  'import.problemCount_other': '{count} things to look at',
  'import.problems': 'What it could not read',
  'import.rowNumber': 'Row {row}',
  'import.andMore': '…and {count} more',
  'import.done_one': 'Imported {count} task',
  'import.done_other': 'Imported {count} tasks',
  'import.action': 'Import tasks…',
  'import.fieldTitle': 'Title',
  'import.fieldDescription': 'Description',
  'import.fieldState': 'State',
  'import.fieldType': 'Type',
  'import.fieldPriority': 'Priority',
  'import.fieldAssignee': 'Assignee',
  'import.fieldLabels': 'Labels',
  'import.fieldParent': 'Parent task',
  'import.fieldBlocks': 'Blocks',
  'import.fieldBlockedBy': 'Blocked by',
  'import.secondPass': 'Parents and blocking links are resolved after every row exists, by key or by title. Anything naming a task that is not in the file is reported rather than guessed at.',
  'import.fieldDue': 'Due date',
  'import.fieldStart': 'Start date',
  'import.fieldEstimate': 'Estimate',
  'import.fieldExternalId': 'Original ID',
  'select.barLabel': 'Actions for the selected tasks',
  'select.count_one': '{count} selected',
  'select.count_other': '{count} selected',
  'select.clear': 'Clear',
  'select.clearSection': 'Clear',
  'select.unassign': 'Nobody',
  'select.noCycle': 'No cycle',
  'select.addLabelSection': 'Add a label',
  'select.removeLabelSection': 'Remove label',
  'select.mixedProjects': 'Several projects — states and labels differ per project',
  'select.moveToProject': 'Move to project',
  'select.movedToProject_one': 'Moved {count} task to {project}',
  'select.movedToProject_other': 'Moved {count} tasks to {project}',
  'select.applied_one': 'Changed {count} task',
  'select.applied_other': 'Changed {count} tasks',
  'select.archived_one': 'Archived {count} task',
  'select.archived_other': 'Archived {count} tasks',
  'select.deleted_one': 'Deleted {count} task',
  'select.deleted_other': 'Deleted {count} tasks',
  'select.deleteConfirm_one': 'Delete {count} task? It can be restored under Settings → Data.',
  'select.deleteConfirm_other': 'Delete {count} tasks? They can be restored under Settings → Data.',
  'select.selectRow': 'Select this task',
  'select.selectGroup': 'Select everything in this group',
  'common.more': 'More',
  'common.moreActions': 'More actions',
  'common.openOriginal': 'Open the original',
  'security.twoFactor': 'Two-factor authentication',
  'security.twoFactorHint': 'A six-digit code from an app, on top of your password. The dangerous part is not turning it on — it is losing the phone, so keep the recovery codes.',
  'security.turnOn': 'Turn it on',
  'security.turnOff': 'Turn it off',
  'security.turnOffConfirm': 'Turn two-factor authentication off? Your password alone will be enough again.',
  'security.passwordToTurnOff': 'Your password, to turn it off',
  'security.turnedOff': 'Two-factor is off',
  'security.twoFactorOn': 'On',
  'security.scanHint': 'Add this secret to your authenticator app, then type the code it shows.',
  'security.secret': 'Secret',
  'security.enterCode': 'The code from the app',
  'security.confirm': 'Confirm',
  'security.recoveryTitle': 'Recovery codes',
  'security.recoveryHint': 'Shown once. Each works one time, in place of the app, for the day the phone is gone. Keep them somewhere that is not the phone.',
  'security.recoverySaved': 'I have saved them',
  'security.devices': 'Signed in on',
  'security.devicesHint': 'Every browser signed in as you. Revoking one signs it out immediately.',
  'security.thisDevice': 'This one',
  'security.unknownDevice': 'Unknown device',
  'security.revoke': 'Sign out',
  'security.revoked': 'Signed out',
  'security.noDevices': 'No other devices.',
  'audit.title': 'What happened here',
  'audit.hint': 'Every recorded change in this workspace, newest first. Admins only — the per-task trail is visible to anybody who can see the task.',
  'audit.empty': 'Nothing recorded yet.',
  'audit.more': 'Show older',
  'audit.created': 'created',
  'audit.updated': 'changed {field}',
  'audit.deleted': 'deleted',
  'audit.restored': 'restored',
  'hooks.format': 'Message shape',
  'hooks.formatKolibri': 'Kolibri JSON (signed)',
  'hooks.inTitle': 'Incoming',
  'hooks.inHint': 'Give a service a URL to post to. A commit message that names a task — SRV-12 — gets commented on it, and one that says “fixes SRV-12” moves it to Done. Understands GitHub and GitLab push payloads, and anything sending the same three fields.',
  'hooks.inAdd': 'Create an incoming URL',
  'hooks.inUrl': 'Incoming URL',
  'hooks.inHidden': 'Hidden — reveal to copy it',
  'hooks.inReveal': 'Reveal and copy',
  'hooks.inFailed': 'Only an admin can see that URL',
  'hooks.title': 'Outgoing webhooks',
  'hooks.hint': 'A call out when something happens here — the generic answer to connecting this to anything else. A failure is retried five times over about half an hour, written down either way, and never blocks a write.',
  'hooks.add': 'Add a webhook',
  'hooks.url': 'URL',
  'hooks.events': 'Events',
  'hooks.name': 'What it is for',
  'hooks.lastResult': 'Last delivery',
  'hooks.never': 'Never sent',
  'hooks.urlInvalid': 'A webhook URL starts with http:// or https://',
  'hooks.secret': 'Signing secret',
  'hooks.test': 'Send a test',
  'hooks.tested': 'It answered: {detail}',
  'hooks.testFailed': 'The test did not get through',
  'hooks.deliveries': 'Deliveries',
  'hooks.deliveriesEmpty': 'Nothing has gone out yet',
  'hooks.deliveriesWaiting': 'waiting',
  'hooks.deliveriesGaveUp': 'gave up',
  'hooks.attempts': '{count} attempts',
  'hooks.replay': 'Send again',
  'hooks.replayed': 'On its way again',
  'hooks.replayFailed': 'That could not be sent again',
  'hooks.remove': 'Remove',
  'hooks.enabled': 'On',
  'trash.title': 'Deleted and archived',
  'trash.intro': 'Nothing is really deleted — a delete marks the row and keeps it, which is how two devices agree that it is gone. That also means it can come back.',
  'trash.tabDeleted': 'Deleted',
  'trash.tabArchived': 'Archived',
  'trash.filter': 'Filter by title',
  'trash.restore': 'Restore',
  'trash.restored': '“{title}” is back',
  'trash.emptyDeleted': 'Nothing has been deleted',
  'trash.emptyArchived': 'Nothing has been archived',
  'trash.emptyHint': 'Which is either reassuring or a sign that this workspace is new.',
  'trash.andMore_one': '…and {count} more, not shown',
  'trash.andMore_other': '…and {count} more, not shown',
  'trash.retentionHint': 'Restoring a task does not bring back the project it was in, if that went too — restore the project first.',
  'trash.emptyAction': 'Empty the trash',
  'trash.emptyConfirm_one': 'Remove {count} deleted thing for good? It cannot be restored afterwards, here or on anybody else’s device.',
  'trash.emptyConfirm_other': 'Remove {count} deleted things for good? They cannot be restored afterwards, here or on anybody else’s device.',
  'trash.emptied_one': '{count} thing removed for good',
  'trash.emptied_other': '{count} things removed for good',
  'trash.emptyFailed': 'The trash could not be emptied',
  'trash.emptyHintWhat': 'Emptying removes the rows and the files behind them, and tells every device to forget the same ones. A device that has been offline since before you did it still has its copy until it syncs.',
  'trash.kindTask': 'Task',
  'trash.kindPage': 'Page',
  'trash.kindProject': 'Project',
  'trash.kindProduct': 'Products',
  'trash.kindCycle': 'Cycle',
  'trash.kindModule': 'Module',
  'trash.kindComment': 'Comment',
  'trash.kindChannel': 'Channel',
  'intake.tab': 'Reports',
  'intake.tabNew': 'Waiting',
  'intake.tabHandled': 'Dealt with',
  'intake.emptyNew': 'Nothing is waiting',
  'intake.emptyHandled': 'Nothing has been dealt with yet',
  'intake.emptyHint': 'Reports arrive here from an intake link — a form for people who have no account and should not need one.',
  'intake.from': 'From {who} — not verified',
  'intake.anonymous': 'No name given',
  'intake.accept': 'Accept as a task',
  'intake.acceptAction': 'Create the task',
  'intake.accepted': 'Accepted',
  'intake.acceptedAs': 'Created “{title}”',
  'intake.decline': 'Decline',
  'intake.declineConfirm': 'Decline “{title}”? It stays in the list, marked, so nobody triages it twice.',
  'intake.declined': 'Declined',
  'intake.wasDeclined': 'Declined',
  'intake.failed': 'That did not work',
  'intake.titleHint': 'What somebody outside calls a problem and what the team calls the work are rarely the same sentence.',
  'intake.whatTheySaid': 'What they wrote',
  'intake.bodyHint': 'This becomes the task’s description, with whatever they left of their name.',
  'intake.linkAction': 'The link',
  'intake.linkTitle': 'A form for reports from outside',
  'intake.linkHint': 'A link anybody can open — no account, no session. What they send waits under Reports until somebody accepts it, so nothing from outside lands on the board on its own.',
  'intake.linkName': 'Report a problem',
  'view.saved': 'Saved views',
  'view.savedActions': 'Saved views',
  'view.pin': 'Open this project on this view',
  'view.unpin': 'Stop opening on this view',
  'view.pinned': 'This project now opens on “{name}”',
  'view.unpinned': 'This project opens on the plain list again',
  'view.isDefault': 'Default',
  'view.icon': 'Icon',
  'view.iconHint': 'Optional. A shape is quicker to find in a menu than a name is to read.',
  'view.saveAsNew': 'Save this view…',
  'view.updateSaved': 'Update “{name}”',
  'view.renameSaved': 'Rename or share…',
  'view.deleteSaved': 'Delete this view',
  'view.deleteConfirm': 'Delete the saved view “{name}”? Nothing happens to the tasks in it.',
  'view.deleted': 'Deleted “{name}”',
  'view.leaveSaved': 'Back to the default view',
  'view.saveCreated': 'Saved as “{name}”',
  'view.saveUpdated': 'Updated “{name}”',
  'view.unsavedChanges': 'Changed since it was saved',
  'view.name': 'Name',
  'view.namePlaceholder': 'Urgent and unassigned',
  'view.nameTaken': 'There is already a view with that name here.',
  'view.private': 'Only you',
  'view.shareWithTeam': 'Share with the team',
  'view.sharedHint': 'Everyone who can see this appears in their list too.',
  'view.privateHint': 'Only you see it.',

  /* ------------------------------------------------------------ my work */
  'myWork.title': 'My work',
  'myWork.overdue': 'Overdue',
  'myWork.dueToday': 'Due today',
  'myWork.emptyTitle': 'Nothing assigned to you',
  'myWork.emptyHint': 'Enjoy it, or pick something up from a project.',
  'myWork.createdByYou': 'Created by you',

  /* ----------------------------------------------------------- overview */
  'overview.greetNight': 'Still up, {name}?',
  'overview.greetMorning': 'Good morning, {name}',
  'overview.greetAfternoon': 'Good afternoon, {name}',
  'overview.greetEvening': 'Good evening, {name}',
  'overview.statOpen': 'Open',
  'overview.statSoon': 'Next 7 days',
  'overview.statUnscheduled': 'No date',
  'overview.statDone': 'Finished',
  'overview.lastDays': 'Last 7 days',
  'overview.recent': 'Recently viewed',

  /* -------------------------------------------------------------- inbox */
  'inbox.title': 'Inbox',
  'inbox.unread': 'Unread',
  'inbox.all': 'All',
  'inbox.markAllRead': 'Mark all read',
  'inbox.emptyTitle': 'Inbox zero',
  'inbox.emptyHint': 'Mentions, assignments and comments on your work land here.',

  /* ------------------------------------------------------------- search */
  'search.title': 'Search',
  'search.placeholder': 'Search — or type @ for a person, # for a label, + for a project',
  'search.palettePlaceholder': 'Search tasks, projects and pages…',
  'search.promptTitle': 'What are you looking for?',
  'search.promptHint': 'Type a few words — or a task number like WEB-12 to go straight to it.',
  'search.noResults': 'Nothing found for “{query}”',
  'search.noResultsHint': 'Try fewer words, or another spelling.',
  'search.searching': 'Searching…',
  'search.paletteNoResults': 'Nothing matches “{query}”',
  'search.paletteAll': 'Search everything for “{query}”',
  'search.workOnly': 'A person and a label are things only work carries, so this searches tasks and their comments.',
  'search.groupTasks': 'Tasks',
  'search.groupPages': 'Pages',
  'search.groupBoards': 'Boards',
  'search.boardGone': 'That board is not on this device yet — try again once it has synced.',
  'search.groupProjects': 'Projects',
  'search.groupComments': 'Comments',
  'search.groupMessages': 'Messages',
  'search.groupMail': 'Mail',
  'search.groupCycles': 'Cycles',
  'search.groupModules': 'Modules',
  'search.groupOther': 'Other',
  'search.groupExact': 'The one you named',
  'search.facetPerson': 'Assigned to',
  'search.facetLabel': 'Label',
  'search.facetProject': 'Project',
  'search.suggestPeople': 'People',
  'search.suggestLabels': 'Labels',
  'search.suggestProjects': 'Projects',
  'search.tipPerson': 'a name — whose work it is',
  'search.tipLabel': 'a label the work carries',
  'search.tipProject': 'the project it belongs to',
  'search.tipPhrase': 'an exact wording — "design review" as one',
  'search.tipExclude': 'a word to leave out: -draft',
  'search.removeFilter': 'Remove the filter {name}',
  'search.commentGone': 'That comment is not in this workspace any more',
  'search.messageGone': 'That message is not in this workspace any more',

  /* ------------------------------------------------------------ projects */
  'state.wipLimit': 'Limit',
  'state.moveUp': 'Move up',
  'state.moveDown': 'Move down',
  'state.wipLimitHint': 'How many tasks may sit in this column at once. 0 means no limit. Going over is shown, never refused — a board that will not take a card teaches people to work somewhere else.',
  'state.wipHint': 'Agreed limit: {limit}',
  'state.allowedRoles': 'Who may move work here',
  'state.allowedAnyone': 'Anybody who can write',
  'state.allowedHint': 'Naming a role hides this column from everyone below it, and the server refuses the move as well — including over the API.',
  'baseline.title': 'Baselines',
  'baseline.take': 'Save the plan as it is',
  'baseline.compare': 'Compare with',
  'baseline.none': 'No baseline',
  'baseline.defaultName': 'Plan of {date}',
  'baseline.saved': 'Saved “{name}”',
  'baseline.hint': 'A baseline keeps today’s dates so the timeline can draw what was promised behind what is happening.',
  'baseline.remove': 'Delete baseline',
  'view.gantt': 'Timeline',
  'gantt.hint': 'Drag a bar to move it, its edges to change the dates. Everything blocked by it moves too.',
  'gantt.zoomIn': 'Zoom in',
  'gantt.zoomOut': 'Zoom out',
  'gantt.undated_one': '{count} task with no dates, so not on the timeline',
  'gantt.undated_other': '{count} tasks with no dates, so not on the timeline',
  'page.print': 'Print, or save as PDF',
  'page.printBlocked': 'The browser blocked the print window — allow pop-ups for this site and try again.',
  'transfer.export': 'Export as JSON',
  'transfer.import': 'Import a JSON export',
  'transfer.hint': 'A project as a document: for moving it to another Kolibri, and for reading it. Not a backup — `kolibri backup` copies the database, which has to be exact.',
  'transfer.imported': 'Imported {count} rows',
  'transfer.importedWithGaps': 'Imported {count} rows. Nobody here matched: {names} — their work arrived unassigned.',
  'transfer.failed': 'That file could not be read',
  'share.action': 'Share a link',
  'share.title': 'Share a read-only link',
  'share.hint': 'Anybody with the link can read this — no account, no sign-in. The link is the whole of the permission, so treat it as one.',
  'share.none': 'No link yet.',
  'share.make': 'Create a link',
  'share.making': 'Creating…',
  'share.minting': 'Preparing the link…',
  'share.link': 'Link',
  'share.revoke': 'Turn this link off',
  'share.revokeConfirm': 'Turn this link off? Anybody holding it stops being able to read.',
  'share.allowComments': 'Let people leave a note',
  'share.allowCommentsHint': 'A box at the bottom of the page. What somebody writes lands in this page’s comments, marked as coming from outside; nothing already said here is shown to them.',
  'comment.edit': 'Edit this comment',
  'comment.edited': 'edited',
  'suggest.action': 'Suggest',
  'suggest.title': 'Suggest a change',
  'suggest.replace': 'Replace',
  'suggest.with': 'with',
  'suggest.removed': '(remove it)',
  'suggest.accept': 'Accept',
  'suggest.reject': 'Reject',
  'suggest.draftLabel': 'Suggesting a change to:',
  'suggest.replaceWith': 'Replace with',
  'suggest.send': 'Suggest',
  'suggest.why': 'Why? (optional)',
  'suggest.sent': 'Suggestion sent — it is in the comments',
  'comment.resolve': 'Resolve',
  'comment.reopen': 'Reopen',
  'comment.resolved': 'Resolved by {name} · {time}',
  'comment.accepted': 'Accepted by {name} · {time}',
  'comment.rejected': 'Rejected by {name} · {time}',
  'comment.showSettled': 'Show settled comments ({count})',
  'comment.hideSettled': 'Hide settled comments ({count})',
  'comment.fromOutside': 'from outside — name not verified',
  'comment.anonymous': 'Someone outside',
  'share.expires': 'Expires',
  'share.includeDone': 'Include finished work',
  'share.opened_one': 'Opened once',
  'share.opened_other': 'Opened {count} times',
  'annotate.comment': 'Comment on this',
  'annotate.jump': 'Show the passage this is about',
  'annotate.orphan': 'the text this pointed at has changed',
  'annotate.orphanHint': 'This comment was made on a passage that is no longer in the page. It is kept, because what somebody said still counts.',
  'annotate.clear': 'Comment on the page instead',
  'annotate.hint': 'Select any text on this page to comment on that passage.',
  'push.title': 'Notifications on this device',
  'push.hint': 'A banner when something needs you, even with the app closed. Per device, because permission belongs to a browser — and the message is fetched by this device when it wakes, so nothing of yours sits on a push service.',
  'push.turnOn': 'Turn on for this device',
  'push.turnOff': 'Turn off for this device',
  'push.on': 'This device will be notified',
  'push.denied': 'This browser has blocked notifications for Kolibri. It has to be undone in the browser’s own site settings — a site cannot ask twice.',
  'push.failed': 'Could not turn notifications on',

  /* ---------------------------------------------------------------- chat */
  'chat.title': 'Chat',
  'chat.new': 'New channel',
  'chat.noneYet': 'No conversations yet. Start a channel, or write to somebody below.',
  'chat.people': 'People',
  'chat.findPerson': 'Find somebody…',
  'chat.findTitle': 'Write to somebody',
  'chat.findLabel': 'Name or email',
  'chat.findPlaceholder': 'Ada, or ada@…',
  'chat.findNobody': 'Nobody on this instance matches that.',
  'chat.aloneHint': 'You are the only person in this workspace — a second account gets one of its own. A direct conversation needs no workspace in common, so look for anybody on this instance below.',
  'chat.pickTitle': 'Pick a conversation',
  'chat.pickHint': 'Channels are for the whole team; a direct message is between the two of you. Both work offline — what you write on a train sends itself when the tunnel ends.',
  'chat.emptyStream': 'Nothing here yet. Say something.',
  'chat.edited': 'edited',
  'chat.today': 'Today',
  'chat.yesterday': 'Yesterday',
  'chat.find': 'Find in this conversation',
  'chat.findPlaceholderHere': 'Find a word somebody said…',
  'chat.matches_one': '{count} match',
  'chat.matches_other': '{count} matches',
  'chat.noMatches': 'Nothing in this conversation says that.',
  'chat.inContext': 'in context',
  'chat.earlier_one': 'Show {count} earlier message',
  'chat.earlier_other': 'Show {count} earlier messages',
  'chat.unsent': 'sending',
  'chat.unsentHint': 'Written here and waiting for a connection. It sends itself.',
  'chat.emptyHint': 'Write @ for somebody, # for a task or project, and paste a screenshot straight in.',
  'chat.replyGone': 'the message this answered is gone',
  'chat.quoted': '{name}: {said}',
  'chat.jumpToReplied': 'Go to the message this answers',
  'chat.youSaid': 'You',
  'chat.waiting_one': '{count} new message',
  'chat.waiting_other': '{count} new messages',
  'chat.messageActions': 'What to do with this message',
  'chat.reply': 'Reply to this',
  'chat.replyingTo': 'Replying to {name}',
  'chat.deleteMessage': 'Delete this message?',
  'chat.placeholder': 'Message {where}',
  'chat.send': 'Send',
  'chat.enterHint': 'Enter sends · Shift+Enter for a new line',
  'presence.online': 'Online',
  'presence.offline': 'Offline',
  'chat.typing': '{name} is typing…',
  'chat.typingTwo': '{first} and {second} are typing…',
  'chat.typingMany': 'Several people are typing…',
  'chat.readOnly': 'Guests can read this conversation but not write in it.',
  'chat.manage': 'Who is in here',
  'chat.members_one': '{count} person',
  'chat.members_other': '{count} people',
  'chat.opened': 'opened it',
  'chat.leave': 'Leave',
  'chat.remove': 'Remove',
  'chat.confirmLeave': 'Leave this channel? You will stop seeing it, and somebody in it has to add you back.',
  'chat.addSomebody': 'Add somebody',
  'chat.added': '{name} can see this channel now',
  'chat.whoCanAdd': 'Who can add and remove people',
  'chat.whoCanAddHint': 'Set per channel: a team channel and one a client can see want different answers.',
  'chat.policyMembers': 'Anybody in the channel',
  'chat.policyAdmins': 'Only whoever opened it, and workspace admins',
  'chat.openChannelHint': 'This channel is open, so everybody in the workspace is already in it. Make it private to keep a member list.',
  'chat.closing': 'Closing it down',
  'chat.closingHint': 'Archiving hides the channel and refuses new messages, and keeps everything said in it. Deleting hides the room the same way and can be undone from the trash — nothing anybody wrote is destroyed until the trash is emptied.',
  'chat.archive': 'Archive',
  'chat.archived': 'Channel archived',
  'chat.confirmDelete': 'Delete this channel? It goes to the trash, and everything said in it goes with it — restorable until the trash is emptied.',
  'chat.backToList': 'Back to conversations',
  'chat.notify': 'Tell me about',
  'chat.notify.all': 'Every message',
  'chat.notify.mentions': 'Only when I am named',
  'chat.notify.none': 'Nothing',
  'chat.newTitle': 'New channel',
  'chat.name': 'Name',
  'chat.willBeCalled': 'It will be called {name}',
  'chat.renamed': 'Now called {name}',
  'chat.nameTakenArchived': 'An archived channel has that name. Bring it back from Settings → Data, or pick another.',
  'chat.whoElse': 'Who else is in it',
  'chat.archivedChannels_one': '{count} archived channel',
  'chat.archivedChannels_other': '{count} archived channels',
  'chat.nameTaken': 'There is already a channel with that name.',
  'chat.topic': 'Topic',
  'chat.topicHint': 'What this channel is for',
  'chat.private': 'Private',
  'chat.privateHint': 'Only people you add can see it, and it stays out of search for everybody else.',
  'chat.create': 'Create channel',
  'chat.created': 'Created {name}',
  'telegram.title': 'Telegram',
  'telegram.hint': 'The one channel that reaches a phone in a second without a browser having to be open. Connecting it starts a chat from your side — Kolibri never learns your number, and a bot cannot message anybody who has not written to it first.',
  'telegram.connect': 'Connect Telegram',
  'telegram.waiting': 'Telegram should be opening. Tap Start there, and this page will notice. The code is good for fifteen minutes and works once.',
  'telegram.connectedTitle': 'Connected',
  'telegram.connectedBody': 'Notifications arrive in your Telegram chat. Sending /stop there disconnects it just as well as the button here.',
  'telegram.nowConnected': 'Telegram is connected',
  'telegram.disconnect': 'Disconnect',
  'telegram.disconnected': 'Telegram disconnected',
  'telegram.sendTest': 'Send a test message',
  'telegram.testSent': 'Sent — check Telegram',
  'telegram.failed': 'Telegram did not accept that',
  'telegram.about': 'Send to Telegram',
  'telegram.all': 'Everything',
  'telegram.allHint': 'Every notification, as it happens.',
  'telegram.important': 'Only what needs me',
  'telegram.importantHint': 'Assignments, mentions, direct messages and due dates. The rest stays in the app.',
  'telegram.none': 'Nothing',
  'telegram.noneHint': 'Stay connected without being messaged. Useful for turning it off for a week.',
  'mail.suppressed': 'Addresses being refused',
  'mail.suppressedHint': 'An address that hard-bounced or reported a message as spam is not written to again. Clear one once it works — a full mailbox is usually temporary, a closed account is not.',
  'mail.suppressedNone': 'Nothing is being refused.',
  'mail.allowAgain': 'Write to it again',
  'nav.planner': 'Team planner',
  'planner.title': 'Team planner',
  'planner.hint': 'Drag a bar sideways to move the dates — whatever is blocked by it moves too — or onto another row to hand it over. Load is counted in tasks running at once, not in hours: estimates here are points, and an invented hour figure is worse than none.',
  'planner.limit': 'Comfortable at once',
  'planner.weeks': 'Weeks',
  'planner.unassigned': 'Nobody yet',
  'planner.loadOn': '{date}: {count} running',
  'planner.peak': 'Busiest day: {peak} at once · {count} in this window',
  'planner.nothingDated': 'Nothing in this window has dates yet, so there is nothing to lay out. Give a task a start or a due date and it appears here.',
  'planner.emptyTitle': 'Nobody to plan for yet',
  'planner.emptyHint': 'Invite the team and their work shows up here, one row each.',
  'nav.portfolio': 'Portfolio',
  'portfolio.title': 'Portfolio',
  'portfolio.projects': 'Projects',
  'portfolio.open': 'Open tasks',
  'portfolio.acrossAll': 'across every project',
  'portfolio.finishedMonth': 'Finished',
  'portfolio.overdue': 'Overdue',
  'portfolio.overdueHint': 'past their due date',
  'portfolio.overdueNone': 'nothing is late',
  'portfolio.timeLogged': 'Time logged',
  'portfolio.roadmap': 'Roadmap',
  'portfolio.roadmapCaption': 'Each bar runs from a project’s start to its target date. The fill is how much of it is finished; the line is today.',
  'portfolio.late': 'late',
  'portfolio.lateHint': 'Past its target date with work still open',
  'portfolio.project': 'Project',
  'portfolio.progress': 'Progress',
  'portfolio.doneOf': '{done} of {total} done',
  'portfolio.openByProject': 'Open work per project',
  'portfolio.status': 'Where everything stands',
  'portfolio.emptyTitle': 'Nothing to line up yet',
  'portfolio.emptyHint': 'The portfolio compares projects — their dates, their progress and where the work is piling up. Create a second project and it fills itself in.',
  'portfolio.noDates': 'No project has a start or a target date yet, so there is nothing to lay out on a timeline.',
  'portfolio.undated': 'Not on the timeline, for want of dates: {names}',
  'project.startDate': 'Start date',
  'project.statusLabel': 'Status',
  'project.parent': 'Part of',
  'project.parentNone': 'Nothing — this is a top-level project',
  'project.parentHint': 'Nesting groups projects in the sidebar and rolls them up in the portfolio. It does not change who can see what.',
  'project.isContainer': 'This project only holds other projects',
  'project.isContainerHint': 'No board and no tasks of its own — it becomes a place to group projects under. You can turn this off again at any time.',
  'project.isContainerBusy': 'Not while there are {count} tasks in it. Move or delete them first.',
  'project.containerEmpty': 'Nothing under this one yet',
  'project.containerEmptyHint': 'This project holds other projects. Add one and it will appear here.',
  'project.containerCount': '{count} projects',
  'project.doneOf': '{done} of {total} done',
  'portfolio.lateCount': '{count} late',
  'projectStatus.planned': 'Planned',
  'projectStatus.in_progress': 'In progress',
  'projectStatus.paused': 'Paused',
  'projectStatus.completed': 'Completed',
  'projectStatus.cancelled': 'Cancelled',
  'copy.action': 'Copy this project',
  'copy.title': 'Copy project',
  'copy.hint': 'Any project can be a template. The workflow states, labels and fields always come across; the rest is up to you.',
  'copy.name': 'Name of the copy',
  'copy.key': 'Key',
  'copy.keyAuto': 'Left empty, one is derived from the name.',
  'copy.parent': 'Put it under',
  'copy.include': 'Also copy',
  'copy.members': 'Members',
  'copy.automations': 'Templates and rules',
  'copy.pages': 'Pages',
  'copy.tasks': 'Tasks',
  'copy.doneTasks': 'Including finished ones',
  'copy.tasksHint': 'Tasks arrive without their comments, time entries or files — a copy is a starting point, not an archive.',
  'copy.submit': 'Copy project',
  'copy.working': 'Copying…',
  'copy.done': 'Copied {count} rows',
  'copy.failed': 'Could not copy the project',
  'project.listTitle': 'Projects',
  'project.new': 'New project',
  'project.emptyTitle': 'No projects yet',
  'project.emptyHint': 'A project holds tasks, cycles, modules and its own workflow states.',
  'project.createCta': 'Create a project',
  'project.doneCount': '{done}/{total} done',
  'project.target': 'Target {date}',
  'project.notFound': 'Project not found',
  'project.notFoundHint': 'It may have been deleted or you lost access.',
  'project.name': 'Name',
  'project.icon': 'Icon',
  'project.key': 'Key',
  'project.keyHint': 'Used for task identifiers, e.g. WEB-42.',
  'project.keyChangeHint': 'Identifiers already given out keep the old key — WEB-42 stays WEB-42.',
  'project.keyTaken': '{name} already uses this key — identifiers would name two tasks.',
  'project.keyAuto': 'auto',
  'project.description': 'Description',
  'project.namePlaceholder': 'Website relaunch',
  'project.visibility': 'Visibility',
  'project.visibilityPublic': 'Everyone in the workspace',
  'project.visibilityPrivate': 'Only invited members',
  'project.creating': 'Creating…',
  'project.createSubmit': 'Create project',
  'project.createFailed': 'Could not create the project',
  'project.tabTasks': 'Tasks',
  'project.tabCycles': 'Cycles',
  'project.tabModules': 'Modules',
  'project.tabPages': 'Pages',
  'project.tabSettings': 'Settings',
  'project.lead': 'Lead',
  'project.targetDate': 'Target date',
  'project.stateColour': 'Colour',
  'project.deleteState': 'Delete state',
  'project.workingDays': 'Working days',
  'project.workingDaysHint': 'Which days the timeline counts when it moves a task out of a blocker’s way. Dragging a bar onto a day that is off leaves it there — this is what the scheduler avoids, not what anybody is forbidden.',
  'project.workflowStates': 'Workflow states',
  'project.addState': 'Add state',
  'project.addColumn': 'Add column',
  'board.moveColumn': 'Move column',
  'board.moveColumnLeft': 'Move left',
  'board.moveColumnRight': 'Move right',
  'project.addColumnHint': 'A column is a state. Drag a header to reorder it; rename and recolour in the project settings.',
  'project.newColumnPrompt': 'Column name',
  'project.newStateName': 'New state',
  'project.deleteStateConfirm': 'Delete the state “{name}”? Tasks keep their current state id.',
  'project.labels': 'Labels',
  'project.newLabel': 'New label',
  'project.labelRemoveHint': 'Click to remove',
  'project.archive': 'Archive project',
  'project.unarchive': 'Unarchive project',
  'project.delete': 'Delete project',
  'project.deleteConfirm': 'Delete “{name}” and hide all of its tasks?',
  'project.pagesTitle': 'Pages',
  'project.newPage': 'New page',
  'project.noPages': 'No pages in this project',
  'project.noPagesHint': 'Specs, notes and decisions live here.',

  /* -------------------------------------------------------------- cycles */
  'cycle.title': 'Cycles',
  'cycle.new': 'New cycle',
  'cycle.emptyTitle': 'No cycles yet',
  'cycle.emptyHint': 'Cycles are time-boxed sprints. Tasks can move between them freely.',
  'cycle.active': 'Active',
  'cycle.shared': 'Shared',
  'cycle.sharedCount_one': '{count} project',
  'cycle.sharedCount_other': '{count} projects',
  'cycle.scope': 'Which projects run it',
  'cycle.scopeThis': 'Just this project',
  'cycle.scopeSome': 'Chosen projects',
  'cycle.scopeAll': 'Every project',
  'cycle.scopeThisHint': 'The usual case. Only this project can put work in it.',
  'cycle.scopeSomeHint': 'One fortnight the projects you pick run together, rather than a copy of it in each.',
  'cycle.scopeAllHint': 'Every project in the workspace, including ones made later.',
  'cycle.scopePickOne': 'Pick at least one project.',
  'cycle.scopeStrandTitle_one': '{count} task would stay in this cycle without its project in it',
  'cycle.scopeStrandTitle_other': '{count} tasks would stay in this cycle without their project in it',
  'cycle.scopeStrandMore': 'and {count} more',
  'cycle.scopeStrandHint': 'Nothing is removed — they keep this cycle. Move them, or leave their project in it.',
  'cycle.scopeStrandConfirm_one': 'Save anyway? {count} task stays in this cycle without its project in it.',
  'cycle.scopeStrandConfirm_other': 'Save anyway? {count} tasks stay in this cycle without their project in it.',
  'cycle.sharedHint': 'Every project in this workspace runs this cycle. Deleting it takes it out of all of them.',
  'cycle.sharedSomeHint': 'The projects named on this cycle run it together. Deleting it takes it out of all of them.',
  'cycle.showTasks': 'Show tasks',
  'cycle.open': 'Open cycle',
  'cycle.edit': 'Edit cycle',
  'cycle.name': 'Name',
  'cycle.starts': 'Starts',
  'cycle.ends': 'Ends',
  'cycle.endsOn': 'Ends {date}',
  'cycle.goal': 'Goal',
  'cycle.taskProgress': '{done}/{total} tasks',
  'cycle.pointProgress': '{done}/{total} points',
  'cycle.notFound': 'Cycle not found',

  /* ------------------------------------------------------------- modules */
  'module.title': 'Modules',
  'module.subtitle': 'Longer-running workstreams, in this project or across several',
  'module.placeholder': 'New module, e.g. Checkout v2',
  'module.emptyTitle': 'No modules yet',
  'module.emptyHint': 'Use modules for milestones that span several cycles — or several projects.',
  'module.delete': 'Delete module',
  'module.new': 'New module',
  'module.edit': 'Edit module',
  'module.name': 'Name',
  'module.target': 'Target date',
  'module.notFound': 'Module not found',
  'module.shared': 'Shared',
  'module.sharedCount_one': '{count} project',
  'module.sharedCount_other': '{count} projects',
  'module.scope': 'Which projects work on it',
  'module.scopeThis': 'Just this project',
  'module.scopeSome': 'Chosen projects',
  'module.scopeAll': 'Every project',
  'module.scopeThisHint': 'The usual case. Only this project can put work in it.',
  'module.scopeSomeHint': 'One milestone the projects you pick work towards together, rather than a copy of it in each.',
  'module.scopeAllHint': 'Every project in the workspace, including ones made later.',
  'module.scopePickOne': 'Pick at least one project.',
  'module.scopeStrandTitle_one': '{count} task would stay in this module without its project in it',
  'module.scopeStrandTitle_other': '{count} tasks would stay in this module without their project in it',
  'module.scopeStrandMore': 'and {count} more',
  'module.scopeStrandHint': 'Nothing is removed — they keep this module. Move them, or leave their project in it.',
  'module.scopeStrandConfirm_one': 'Save anyway? {count} task stays in this module without its project in it.',
  'module.scopeStrandConfirm_other': 'Save anyway? {count} tasks stay in this module without their project in it.',
  'module.sharedHint': 'Every project in this workspace works on this module. Deleting it takes it out of all of them.',
  'module.sharedSomeHint': 'The projects named on this module work on it together. Deleting it takes it out of all of them.',
  'module.danger': 'Danger',

  /* --------------------------------------------------------------- pages */
  'page.diagramOpen': 'Open the diagram, to look closely',
  'page.diagramTitle': 'Diagram',
  'page.diagramCanvas': 'Diagram — drag to move, pinch or scroll to zoom',
  'page.diagramIn': 'Zoom in',
  'page.diagramOut': 'Zoom out',
  'page.diagramFit': 'Fit',
  'page.diagramHint': 'Pinch or scroll to zoom, drag to move, double-tap to go in and back out. + and − zoom, arrows move, 0 fits.',
  'page.listTitle': 'Pages',
  'page.new': 'New page',
  'page.emptyTitle': 'No pages yet',
  'page.emptyHint': 'Pages are markdown documents with history — handbooks, specs, meeting notes.',
  'page.writeFirst': 'Write the first page',
  'page.recentlyEdited': 'Recently edited',
  'page.all': 'All pages',
  'page.emptyPage': 'Empty page',
  'page.updated': 'Updated {time}',
  'page.notFound': 'Page not found',
  'page.title': 'Page',
  'page.addSubpage': 'Add sub-page',
  'page.history': 'Version history',
  'page.moveToProject': 'Move to project',
  'page.workspaceLevel': 'Workspace level',
  'page.delete': 'Delete page',
  'page.deleteConfirm': 'Delete “{title}”? Sub-pages stay but lose their parent.',
  'page.mergedIn': 'Somebody else’s change was merged in just now.',
  'page.startWriting': 'This page is empty — start writing',
  'page.subpages': 'Sub-pages',
  'page.labels': 'Labels',
  'page.watch': 'Tell me when this changes',
  'page.unwatch': 'Stop telling me',
  'page.watching': 'Watching',
  'page.access': 'Who can see it',
  'page.accessWorkspace': 'Everyone in the workspace',
  'page.accessProject': 'The project it belongs to',
  'page.accessPrivate': 'Only me',
  'page.accessNeedsProject': 'Move the page into a project first',
  'page.markTemplate': 'Keep as a template',
  'page.unmarkTemplate': 'Stop treating as a template',
  'page.template': 'Template',
  'page.newFromTemplate': 'New from a template',
  'page.noTemplates': 'No templates yet — mark a page as one from its menu.',
  'page.whatChanged': 'What changed',
  'page.diffSummary': '{added} lines added, {removed} removed since this version',
  'page.diffIdentical': 'Nothing has changed since this version.',
  'page.diffSkipped_one': '{count} unchanged line',
  'page.diffSkipped_other': '{count} unchanged lines',
  'page.diffAdded': 'Added:',
  'page.diffRemoved': 'Removed:',
  'page.compare': 'What changed',
  'page.export': 'Download as markdown',
  'page.exported': 'Downloaded — the page and everything under it',
  'page.filterByLabel': 'Filter by label',
  'page.allPages': 'All pages',
  'common.loading': 'Loading…',
  'page.discussion': 'Discussion',
  'page.noComments': 'Nothing said about this page yet. Mention somebody with @ and they will hear about it.',
  'page.byAuthor': 'By {name}',
  'page.updatedAgo': 'updated {time}',
  'page.noVersions': 'No earlier versions yet — history starts with the first edit.',
  'page.restoreConfirm': 'Restore this version? The current text is kept in history.',
  'page.restored': 'Version restored',
  'page.historyFailed': 'Could not load history',
  'page.versionSize': '{count} characters',
  'page.toggleTree': 'Toggle',
  'page.outline': 'On this page',
  'page.graph': 'Graph',
  'page.embedLoop': 'Already shown above:',
  'page.backlinks_one': '1 page links here',
  'page.backlinks_other': '{count} pages link here',
  'page.unwritten': 'Linked to, not written yet',
  'page.unwrittenHint': 'Somebody wrote [[a link]] to these. Open one to write it.',
  'page.wantedBy_one': 'from 1 page',
  'page.wantedBy_other': 'from {count} pages',
  'page.renameLinks_one': 'Renamed — the link on 1 other page follows it.',
  'page.renameLinks_other': 'Renamed — the links on {count} other pages follow it.',
  'page.renameRefused': 'Renamed, but links to it were left alone: a title with [ ] or | in it cannot be linked to.',
  'page.archivedTitle': 'Archived pages',
  'page.archivedHint': 'Out of the tree and out of search, but still here. Restore one to put it back.',
  'page.archivedNotice': 'This page is archived, so it is out of the page list and out of search.',
  'page.dragHint': 'Drag a page onto another to nest it, or between two to reorder.',
  'page.move': 'Move',
  'page.moveUp': 'Move up',
  'page.moveDown': 'Move down',
  'page.moveIn': 'Move under the page above',
  'page.moveOut': 'Move out one level',
  'page.cover': 'Cover',
  'page.coverAdd': 'Add a cover',
  'page.coverReplace': 'Replace the cover',
  'page.coverRemove': 'Remove the cover',
  'page.changeCreated': 'wrote this page',
  'page.changeDeleted': 'deleted it',
  'page.changeTitle': 'renamed it',
  'page.changeParent': 'moved it in the tree',
  'page.changeLabels': 'changed the labels',
  'page.changeAccess': 'changed who can see it',
  'page.changeProject': 'moved it to another project',
  'page.changeArchived': 'archived it',
  'page.changeRestored': 'restored it',
  'page.changeTemplated': 'made it a template',
  'page.changeUntemplated': 'stopped treating it as a template',

  /* -------------------------------------------------- format, in and out */
  'page.format': 'Written in',
  'page.formatMarkdown': 'Markdown',
  'page.formatHtml': 'HTML',
  'page.convertToHtml': 'Rewrite this page as HTML? The markdown is rendered once and what you get back is the result — the old text stays in the history.',
  'page.convertToMarkdown': 'Rewrite this page as markdown? Anything markdown cannot say — a layout, a colspan, a style — is lost. The old text stays in the history.',
  'page.convertAction': 'Rewrite',
  'page.converted': 'Rewritten. The version before it is in the history.',
  'page.download': 'Download',
  'page.exportHtml': 'Download as one HTML file',
  'page.import': 'Import',
  'page.importHere': 'Import files under this page',
  'page.importDrop': 'Drop markdown or HTML files here',
  'page.importChoose': 'Choose files',
  'page.importRefused': 'Not text, so left out: {names}',
  'page.importKeepHtml': 'Keep HTML pages as HTML',
  'page.importSplit': 'Split long documents at their # headings',
  'page.importUnderPage': 'The new pages land under this one.',
  'page.importAtTop': 'The new pages land at the top level.',
  'page.importAction_one': 'Import 1 page',
  'page.importAction_other': 'Import {count} pages',
  'page.imported_one': '1 page imported',
  'page.imported_other': '{count} pages imported',

  /* --------------------------------------------------- tidying the wiki */
  'page.bulkTitle': 'Do one thing to several pages',
  'page.bulkCount_one': '1 page',
  'page.bulkCount_other': '{count} pages',
  'page.bulkApplied_one': '1 page changed',
  'page.bulkApplied_other': '{count} pages changed',
  'page.bulkOrganise': 'Organise',
  'page.bulkMoveUnder': 'Move under',
  'page.bulkTopLevel': 'Top level',
  'page.bulkWithBelow_one': 'Archive with 1 sub-page',
  'page.bulkWithBelow_other': 'Archive with {count} sub-pages',
  'page.bulkBelowHint': 'Sub-pages under the selection. They are not selected; the named actions take them.',
  'page.bulkMovedSome': '{count} of {total} moved — the rest would have sat inside themselves',
  'page.bulkDelete_one': 'Delete 1 page? Its sub-pages go with it, and the trash can bring them back.',
  'page.bulkDelete_other': 'Delete {count} pages? Their sub-pages go with them, and the trash can bring them back.',
  'page.bulkDeleteWithBelow': 'Delete {count} pages and the {below} sub-pages under them? The trash can bring them back.',
  'page.select': 'Select pages',
  'page.foldAll': 'Fold everything',
  'page.unfoldAll': 'Open everything',
  'page.filterTree': 'Find a page by title',
  'page.filterNone': 'No page here is called that',
  'page.sidebarShow': 'Show the tree',
  'page.sidebarHide': 'Hide the tree',
  'page.tidy': 'Tidy up',
  'page.tidyTitle': 'Tidying up',
  'page.tidyIntro': 'What the tree cannot show you: pages whose parent is gone, two pages answering to one title, drafts nobody filled in, and what is in the archive and the trash. Nothing here changes on its own.',
  'page.tidyClean': 'Nothing to tidy',
  'page.tidyCleanHint': 'Every page is in the tree, no two answer to the same title, and none of them is empty.',
  'page.tidyFound_one': '1 page worth a look',
  'page.tidyFound_other': '{count} pages worth a look',
  'page.tidyCountPages': 'In the tree',
  'page.tidyCountTemplates': 'Templates',
  'page.tidyCountArchived': 'Archived',
  'page.tidyCountDeleted': 'In the trash',
  'page.tidyStaleDays': 'Untouched for',
  'page.tidyDays': '{count} days',
  'problem.detached': 'The page above them is gone',
  'problem.detachedWhy': 'Their parent was archived, deleted or turned into a template, so the tree draws them at the top level as though somebody put them there.',
  'problem.duplicate': 'Two pages, one title',
  'problem.duplicateWhy': 'A [[link]] resolves to whichever was written first, so every link meant for the other one lands on the wrong page and nothing says so.',
  'problem.empty': 'Nothing written yet',
  'problem.emptyWhy': 'Started and never filled in. Pages with sub-pages are left out — an empty page holding a chapter is a section heading.',
  'problem.isolated': 'Nothing points at them',
  'problem.isolatedWhy': 'Top level, no sub-pages, and no other page links to them. Reachable only from this list.',
  'page.status': 'Status',
  'page.statusNone': 'No status',
  'page.statusAll': 'Any status',
  'page.statuses': 'Page statuses',
  'page.statusesHint': 'How finished a page is. The names belong to this workspace — the meaning beside each one is what the rest of the product reads: a new page starts on the first draft, the tidying screen reports a review nobody finished, and anything but final says so when a page is shared or exported.',
  'page.statusKind': 'Meaning',
  'page.statusAdd': 'Add a rung',
  'page.statusDelete': 'Delete the rung',
  'page.statusDeleteConfirm': 'Delete “{name}”? Pages on it read as drafts again — none of them is deleted.',
  'page.statusColour': 'Colour of the rung',
  'page.statusNotFinal': '{status} — this text has not been signed off.',
  'page.bulkStatus': 'Set the status',
  'page.tidyReviewDays': 'In review for',
  'pageStatus.draft': 'Draft',
  'pageStatus.review': 'In review',
  'pageStatus.final': 'Final',
  'problem.inReview': 'A review nobody finished',
  'problem.inReviewWhy': 'Somebody put it up to be read and nothing has happened since. Either it was read and nobody said so, or it is waiting on somebody who does not know about it.',
  'problem.stale': 'Nobody has touched them',
  'problem.staleWhy': 'Which is often simply true of a finished document. Here so it can be checked rather than assumed.',
  'page.tidyTrash': 'In the trash',
  'page.tidyTrashHint': 'Deleted pages. Still on this device, because that is what lets two devices agree about a deletion.',
  'page.tidyTrashEmpty': 'Nothing deleted',
  'page.tidyArchiveEmpty': 'Nothing archived',
  'page.broughtBack': '“{title}” is back',

  /* ------------------------------------------------------------- canvas */
  'canvas.kind': 'Canvas',
  'canvas.new': 'New canvas',
  'canvas.addSubcanvas': 'Add a canvas below',
  'board.kind': 'Board',
  'board.new': 'New board',
  'board.addSubboard': 'Add a board below',
  'board.toCanvas': 'Turn into a canvas',
  'board.fromCanvas': 'Show as a board page',
  'board.copyToPage': 'Copy to its own page',
  'board.emptyTitle': 'An empty board',
  'board.emptyHint': 'A board is a styled, interactive page of its own. Start from a template, bring an artifact made with Claude, or describe what it should show.',
  'board.emptyReader': 'Nothing has been put on this board yet.',
  'board.fitScreen': 'Fit to screen',
  'board.asDesigned': 'As designed',
  'board.tooWide': 'Drawn smaller so all of it shows: this board’s layout is wider than the screen.',
  'board.makeFit': 'Make it fit phones',
  'board.makeFitAsk': 'Make this board work on a phone, about 390 px wide, without changing how it looks on a wide screen: let grids and rows of cards wrap (for example grid-template-columns: repeat(auto-fit, minmax(…, 1fr))), put wide tables in a box that scrolls sideways, and give nothing a fixed width wider than the screen. Keep all the content and the look.',
  'canvas.tools': 'Canvas tools',
  'canvas.addBoard': 'Add board',
  'canvas.addNote': 'Note',
  'canvas.comment': 'Comment',
  'canvas.commentTool': 'Pin a comment on a board',
  'canvas.comments': 'Comments',
  'canvas.fit': 'Show everything',
  'canvas.open': 'Open',
  'canvas.play': 'Try it',
  'canvas.noteColor': 'Note colour',
  'canvas.color.yellow': 'Yellow',
  'canvas.color.blue': 'Blue',
  'canvas.color.green': 'Green',
  'canvas.color.pink': 'Pink',
  'canvas.duplicate': 'Duplicate',
  'canvas.toFront': 'Bring to front',
  'canvas.delete': 'Delete',
  'canvas.deleteBoard': 'Delete the board “{title}”?',
  'canvas.note': 'Note',
  'canvas.notePlaceholder': 'Double-click to write',
  'canvas.noteLabel': 'Note: {text}',
  'canvas.boardLabel': 'Board: {title}. Enter opens it.',
  'canvas.pinLabel': 'Comment {n}',
  'canvas.pinOn': 'On {title}',
  'canvas.boardGone': 'A board that was deleted',
  'canvas.emptyHint': 'An empty canvas. Add a board to start from a page, a dashboard or a phone screen — or ask Claude to draw one.',
  'canvas.emptyReadOnly': 'Nothing on this canvas yet.',
  'canvas.pinHint': 'Click on a board to pin a comment there.',
  'canvas.noComments': 'No comments yet. Use Comment to pin one on a board.',
  'canvas.template.blank': 'Blank page',
  'canvas.template.document': 'Document',
  'canvas.template.dashboard': 'Dashboard',
  'canvas.template.phone': 'Phone screen',
  'canvas.drag': 'Drag to move',
  'canvas.editing': 'Editing {title}',
  'canvas.boardTitle': 'Board name',
  'canvas.view': 'View',
  'canvas.design': 'Design',
  'canvas.source': 'Source',
  'canvas.undo': 'Undo',
  'canvas.redo': 'Redo',
  'canvas.inspector': 'Properties',
  'canvas.insert': 'Add to the board',
  'canvas.insertAfter': 'Add below the selection',
  'canvas.selectHint': 'Click something on the board to change it. Double-click text to type in place; drag the grip to move it.',
  'canvas.readOnly': 'You can look at this board but not change it.',
  'canvas.board': 'Board',
  'canvas.width': 'Width',
  'canvas.height': 'Height',
  'canvas.path': 'Where the selection sits',
  'canvas.moveUp': 'Move up',
  'canvas.moveDown': 'Move down',
  'canvas.deleteElement': 'Delete this element',
  'canvas.table': 'Table',
  'canvas.rowAbove': 'Row above',
  'canvas.rowBelow': 'Row below',
  'canvas.columnLeft': 'Column left',
  'canvas.columnRight': 'Column right',
  'canvas.removeRow': 'Delete row',
  'canvas.removeColumn': 'Delete column',
  'canvas.text': 'Text',
  'canvas.link': 'Link',
  'canvas.style': 'Look',
  'canvas.style.color': 'Text colour',
  'canvas.style.background': 'Background',
  'canvas.style.fontSize': 'Font size',
  'canvas.style.fontWeight': 'Weight',
  'canvas.style.align': 'Alignment',
  'canvas.style.padding': 'Padding',
  'canvas.style.margin': 'Margin',
  'canvas.style.gap': 'Gap',
  'canvas.style.border': 'Border',
  'canvas.style.radius': 'Corner radius',
  'canvas.style.width': 'Width',
  'canvas.elementGone': 'That element changed while you were editing — select it again.',
  'canvas.block.heading': 'Heading',
  'canvas.block.text': 'Text',
  'canvas.block.box': 'Box',
  'canvas.block.columns': 'Two columns',
  'canvas.block.table': 'Table',
  'canvas.block.list': 'List',
  'canvas.block.button': 'Button',
  'canvas.block.divider': 'Divider',
  'canvas.block.callout': 'Callout',
  'canvas.word.title': 'Untitled',
  'canvas.word.heading': 'Heading',
  'canvas.word.text': 'Write something here.',
  'canvas.word.item': 'Item',
  'canvas.word.button': 'Button',
  'canvas.word.column': 'Column',
  'canvas.word.metric': 'Metric',
  'canvas.history': 'History',
  'canvas.describe': 'Describe it to {name}…',
  'canvas.describeModel': 'Describe it to the model…',
  'canvas.ask': 'Ask {name}',
  'canvas.askModel': 'Ask the model',
  'canvas.whatToDraw': 'What should the board show?',
  'canvas.whatToChange': 'What should change?',
  'canvas.drawPlaceholder': 'A sales pipeline board: deals by stage as columns, a call list for today, and the quarter’s target against what is closed.',
  'canvas.changePlaceholder': 'Make the header dark blue, add a column for the owner, and sort by value.',
  'canvas.format': 'Format',
  'canvas.format.page': 'Page',
  'canvas.format.wide': 'Wide screen',
  'canvas.format.slide': 'Slide',
  'canvas.format.phone': 'Phone',
  'canvas.matchLook': 'Match the look of the boards already here',
  'canvas.draw': 'Draw it',
  'canvas.change': 'Change it',
  'canvas.drawing': 'Drawing…',
  'canvas.drawingHint': 'A whole board takes a minute or two.',
  'canvas.draftPrivacy': 'Your description, this canvas’s title and — to match their look — up to two of its boards are sent to the model this server is configured with.',
  'canvas.draftFailed': 'The board could not be drawn. Try again in a moment.',
  'canvas.importTitle': 'Import HTML…',
  'canvas.importFile': 'An HTML file — for example an artifact downloaded from Claude',
  'canvas.importPaste': 'Or paste the HTML',
  'canvas.importAction': 'Put it on the canvas',
  'canvas.importHint': 'Kept exactly as it is, scripts included. A board reaches no network, so anything it loads from elsewhere — a CDN script, a web font, a picture by URL — will be missing.',
  'canvas.importNotHtml': 'That does not look like HTML.',
  'canvas.imported': 'Imported board',
  'canvas.pdf': 'PDF',
  'canvas.exportPdf': 'Export as PDF — one page per board; choose “Save as PDF” in the print dialogue',
  'canvas.preparingPdf': 'Drawing the boards for the PDF…',
  'canvas.block.image': 'Image',
  'canvas.image': 'Image',
  'canvas.imageReplace': 'Replace image',
  'canvas.imageAlt': 'Description',
  'canvas.uploading': 'Uploading…',
  'canvas.dropImages': 'Drop images to put them on the canvas',
  'canvas.noVersions': 'No earlier versions yet. One is kept before each sitting of changes.',
  'canvas.restoreConfirm': 'Put this board back as it was then? Its current state is kept in the history.',

  /* ---------------------------------------------------------- the vault */
  'secret.title': 'Secrets',
  'secret.new': 'New secret',
  'secret.create': 'Keep it',
  'secret.edit': 'Edit',
  'secret.delete': 'Delete secret',
  'secret.deleteConfirm': 'Delete “{name}”? The value goes with it and cannot be got back.',
  'secret.saved': 'Saved',
  'secret.created': 'Kept',
  'secret.saveFailed': 'Could not save that',
  'secret.promise': 'Values are encrypted with a key kept outside the database, so a copied backup, an export or a synced device carries none of them. Whoever runs this server can still read them — that is what self-hosting means.',
  'secret.emptyTitle': 'Nothing kept here yet',
  'secret.emptyHint': 'API keys, passwords, connection strings — the things that otherwise end up pasted into a page or a chat message, where everybody has them for ever and nobody knows who read them.',
  'secret.scopeAll': 'All',
  'secret.name': 'What it is',
  'secret.namePlaceholder': 'Stripe live key',
  'secret.value': 'The value',
  'secret.valuePlaceholder': 'Paste it here',
  'secret.valueKeep': 'Leave blank to keep the current value',
  'secret.description': 'Note',
  'secret.descriptionPlaceholder': 'Where it is used, who issued it, how to replace it',
  'secret.kind': 'Kind',
  'secret.kindPassword': 'Password',
  'secret.kindApiKey': 'API key',
  'secret.kindToken': 'Token',
  'secret.kindCertificate': 'Certificate',
  'secret.kindConnection': 'Connection string',
  'secret.kindNote': 'Secure note',
  'secret.access': 'Who can read it',
  'secret.accessWorkspace': 'Everyone in the workspace',
  'secret.accessProject': 'The project it belongs to',
  'secret.accessPrivate': 'Only me',
  'secret.accessHint.workspace': 'Every member can reveal it, and every reveal is recorded.',
  'secret.accessHint.project': 'Only people who can see the project. Pick one below.',
  'secret.accessHint.private': 'Nobody else, including an owner. There is no override.',
  'secret.project': 'Project',
  'secret.pickProject': 'Pick a project',
  'secret.rotateEvery': 'Rotate every (days)',
  'secret.rotateHint': '0 for no cadence. With one, the list says when it is due and shouts when it is late — which is the risk in a shared vault, not the encryption.',
  'secret.reveal': 'Reveal',
  'secret.hide': 'Hide',
  'secret.hidesIn': 'Hides in {count}s',
  'secret.copied': 'Copied',
  'secret.revealFailed': 'Could not open that',
  'secret.rotatedAgo': 'set {time}',
  'secret.usedAgo': 'read {time}',
  'secret.neverSet': 'no value yet',
  'secret.byAuthor': 'by {name}',
  'secret.overdue': 'Overdue',
  'secret.dueIn': 'Due in {count}d',
  'secret.overdueCount_one': '1 overdue',
  'secret.overdueCount_other': '{count} overdue',
  'secret.history': 'Who has read it',
  'secret.neverRead': 'Nobody has revealed this yet.',
  'secret.readBy': 'Read by {names} — last {time}',
  'secret.strength.weak': 'Short. Length does more than punctuation does.',
  'secret.strength.fair': 'Reasonable.',
  'secret.strength.strong': 'Strong.',
  /* Environments: the second axis on a secret. */
  'environment.title': 'Environments',
  'environment.explain': 'The same key can hold a different value in each environment, and there is no fallback between them — a process asking for production never quietly receives the development value. A secret in no environment is the same everywhere. The rank sets who may open one at all.',
  'environment.name': 'Name',
  'environment.minRole': 'Who may open it',
  'environment.roleMember': 'Everyone in the workspace',
  'environment.roleAdmin': 'Administrators',
  'environment.roleOwner': 'The owner',
  'environment.holdsNone': 'Nothing kept here yet',
  'environment.holds_one': '1 secret',
  'environment.holds_other': '{count} secrets',
  'environment.stillHolds_one': '{name} still holds 1 secret. Move or delete it first.',
  'environment.stillHolds_other': '{name} still holds {count} secrets. Move or delete them first.',
  'environment.exists': 'There is already an environment called {name}.',
  'environment.add': 'Add',
  'environment.addPlaceholder': 'qa, sandbox, eu…',
  'environment.adminOnly': 'Environments are set up by an administrator of the workspace.',
  'secret.environment': 'Environment',
  'secret.everywhere': 'The same everywhere',
  'secret.environmentHint': 'Which environment’s value this is. Left as “the same everywhere”, it answers for all of them — and nothing falls back from one to another.',
  'secret.manageEnvironments': 'Environments',
  'secret.scopeAllEnvironments': 'Every environment',

  /* --------------------------------------------------------------- teams */
  'team.title': 'Teams',
  'team.placeholder': 'New team, e.g. Platform',
  'team.newLabel': 'Name of the new team',
  'team.nameLabel': 'Team name',
  'team.emptyTitle': 'No teams yet',
  'team.emptyHint': 'Teams group people and projects — useful once more than a handful of people share the workspace.',
  'team.members': 'Members',
  'team.noMembers': 'No members yet',
  'team.projectCount_one': '{count} project',
  'team.projectCount_other': '{count} projects',
  'team.tasksDone': '{done}/{total} tasks done',
  'team.addProject': 'project',
  'team.delete': 'Delete team',
  'team.deleteConfirm': 'Delete the team “{name}”? Projects stay, they just lose the team.',
  'team.danger': 'Danger',

  /* --------------------------------------------------------------- login */
  'field.settingsTitle': 'Fields',
  'field.settingsHint': 'Extra fields on this project’s tasks. Limit one to a work item type and only that kind of task is asked for it.',
  'field.sectionTitle': 'Fields',
  'field.name': 'Field name',
  'field.kind': 'Kind',
  'field.kind.text': 'Text',
  'field.kind.long_text': 'Long text',
  'field.kind.number': 'Number',
  'field.kind.select': 'One of',
  'field.kind.multi_select': 'Several of',
  'field.kind.date': 'Date',
  'field.kind.checkbox': 'Yes or no',
  'field.kind.url': 'Link',
  'field.kind.person': 'Person',
  'field.choices': 'Choices',
  'field.choicesPlaceholder': 'Low, Medium, High',
  'field.help': 'Explanation',
  'field.appliesTo': 'Asked on',
  'field.appliesToAll': 'Every type',
  'field.appliesToHint': 'Pick types to ask only there — a bug can want steps to reproduce while a feature does not.',
  'field.required': 'Ask for it',
  'field.requiredHint': 'Marks the field as wanted. Nothing is refused without it: a task can arrive from a rule, the API or a phone that was offline.',
  'field.showInTable': 'Offer as a column in the table view',
  'field.options': 'Settings for this field',
  'field.add': 'Add a field',
  'field.newName': 'New field',
  'field.remove': 'Remove field',
  'field.removeConfirm': 'Remove “{name}” and every answer to it?',
  'field.yes': 'Yes',
  'field.no': 'No',
  'field.answered': 'Has an answer',
  'field.noAnswer': 'No answer',
  'field.noValue': 'Not set',
  'field.noOptions': 'No choices yet — add some in the project settings.',
  'field.wanted': '· wanted',
  'field.open': 'Open link',
  /* -------------------------------------------------- the way in */
  'server.title': 'Which Kolibri?',
  'server.explain': 'This app talks to your own server. Enter its address once — you can change it later by signing out.',
  'server.address': 'Server address',
  'server.connect': 'Connect',
  'server.checking': 'Checking…',
  'server.unreachable': 'No Kolibri answered at that address. Check the address, and that the server is reachable from this device.',
  'auth.headline': 'Your work lives on your server.',
  'auth.blurb': 'Projects, tasks and pages — offline first, with an assistant allowed to read along.',
  'auth.factOffline': 'Keeps working when the network does not',
  'auth.factPrivate': 'Nothing leaves the machine you run it on',
  'auth.factAssistant': 'MCP: an assistant reads and writes with you',
  'auth.licence': 'Self-hosted · Open source · AGPL-3.0',
  'auth.haveAccount': 'Already have one?',
  'auth.noAccount': 'No account yet?',
  'auth.language': 'Language',

  'login.setupTitle': 'Set up Kolibri',
  'login.welcomeBack': 'Welcome back',
  'login.createAccount': 'Create your account',
  'login.firstAccountHint': 'The first account owns this instance',
  'login.tagline': 'Projects, tasks and pages — offline first',
  'login.invitedTo': 'You were invited to {workspace}',
  'login.yourName': 'Your name',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.passwordHint': 'At least 8 characters.',
  'login.workspaceName': 'Workspace name',
  'login.workspacePlaceholder': 'Acme',
  'login.signIn': 'Sign in',
  'login.createInstance': 'Create instance',
  'login.createAccountSubmit': 'Create account',
  'login.footer': 'Self-hosted · open source',
  'login.inviteInvalid': 'This invite link is not valid any more.',
  'login.joinTitle': 'Join a workspace',
  'login.signedInAs': 'You are signed in as {email}.',
  'login.joinSubmit': 'Join',
  'login.useAnotherAccount': 'Use a different account',
  'login.ssoContinue': 'Continue with {provider}',
  'login.ssoOr': 'or',
  'login.ssoOnlyHint': 'This instance signs in through your identity provider.',

  /* ------------------------------------------------------------ settings */
  'settings.title': 'Settings',
  'settings.tabProfile': 'Profile',
  'settings.tabNotifications': 'Notifications',
  'settings.tabWorkspace': 'Workspace',
  'settings.tabMembers': 'Members',
  'settings.tabApi': 'API & MCP',
  'settings.tabData': 'Data',
  'settings.tabInstance': 'Server',
  'instance.intro': 'The settings that belong to this server rather than to a workspace. What you type here wins over what the container was started with, and clearing a field hands it back.',
  'instance.mail': 'Email',
  'instance.mailHint': 'The relay everything leaves through: invitations, password resets, notification digests.',
  'instance.telegram': 'Telegram',
  'instance.telegramHint': 'One bot token is the whole of it — everybody connects their own chat themselves, from their own Telegram.',
  'instance.ai': 'Model',
  'instance.aiHint': 'The model that reads a task back. Off until a key is here, and asked only in workspaces that switched it on.',
  'instance.transport': 'How mail leaves',
  'instance.smtpHost': 'Relay host',
  'instance.smtpPort': 'Port',
  'instance.smtpEncryption': 'Encryption',
  'instance.smtpUser': 'Username',
  'instance.smtpPass': 'Password',
  'instance.smtpInsecure': 'Accept an invalid certificate',
  'instance.mailFrom': 'Sender address',
  'instance.mailFromName': 'Sender name',
  'instance.mailReplyTo': 'Reply to',
  'instance.scalewayKey': 'Scaleway secret key',
  'instance.scalewayProject': 'Scaleway project',
  'instance.botToken': 'Bot token',
  'instance.aiProvider': 'Provider',
  'instance.aiKey': 'API key',
  'instance.aiModel': 'Model name',
  'instance.aiBaseUrl': 'Base address',
  'instance.fromEnvironment': 'from the environment',
  'instance.automatic': 'Automatic',
  'instance.secretSet': 'saved — leave empty to keep it',
  'instance.secretUnset': 'not set',
  'instance.clear': 'Clear this setting',
  'instance.save': 'Save',
  'instance.saved': 'Saved',
  'instance.saveFailed': 'That could not be saved',
  'instance.unsaved': 'not saved yet',
  'instance.test': 'Send a test',
  'instance.testFailed': 'The test did not get through',
  'instance.testMailOk': 'A test message is on its way to {email}',
  'instance.testTelegramOk': '{bot} answered. Connect your own chat below to receive a message too.',
  'instance.testTelegramSent': '{bot} answered, and a message is on its way to your chat',
  'instance.testAiOk': '{model} answered',
  'instance.on': 'on',
  'instance.off': 'off',
  'instance.yes': 'Yes',
  'instance.no': 'No',
  'instance.backup': 'Backups',
  'instance.backupHint': 'Where the nightly snapshot goes. A bucket keeps everything, files included, and a new instance given the same bucket restores from it; an address is sent the database as a .zip — as readable as the inbox it lands in. Left empty, the endpoint and keys are the file storage’s. The hour is the server’s own.',
  'instance.backupHour': 'Hour',
  'instance.backupKeep': 'Snapshots kept in the directory',
  'instance.backupEmail': 'Send to address',
  'instance.backupBucket': 'Bucket',
  'instance.backupEndpoint': 'Endpoint',
  'instance.backupRegion': 'Region',
  'instance.backupAccessKey': 'Access key',
  'instance.backupSecretKey': 'Secret key',
  'instance.backupDirectory': 'directory',
  'instance.backupBucketChip': 'bucket',
  'instance.backupEmailChip': 'email',
  'instance.testBackupBoth': 'Wrote a test object to {bucket} and read it back; a test with an attachment is on its way to {email}',
  'instance.testBackupBucket': 'Wrote a test object to {bucket} and read it back',
  'instance.testBackupEmail': 'A test with an attachment is on its way to {email}',
  'instance.inherited': 'applies while this is empty',
  'instance.unreadable': 'Saved under a different instance secret (after a restore, for example) — it cannot be read here. Type it in again.',
  'instance.secretInherited': 'the file storage’s key applies while this is empty',

  'profile.displayName': 'Display name',
  'profile.changePicture': 'Change picture',
  'profile.removePicture': 'Remove',
  'profile.pictureChanged': 'Picture updated',
  'profile.bio': 'Bio',
  'profile.save': 'Save profile',
  'profile.saved': 'Profile saved',
  'profile.appearance': 'Appearance',
  'profile.themeSystem': 'system',
  'profile.themeLight': 'light',
  'profile.themeDark': 'dark',
  'profile.language': 'Language',
  'profile.languageUnreviewed': 'machine-written, unreviewed',
  'profile.languageHint': 'Applies to this browser, and to the emails you receive.',
  'profile.password': 'Password',
  'profile.currentPassword': 'Current password',
  'profile.newPassword': 'New password',
  'profile.changePassword': 'Change password',
  'profile.passwordChanged': 'Password changed — other devices were signed out',
  'profile.passwordFailed': 'Could not change the password',

  'notify.intro': 'Everything always lands in your in-app inbox. Email is an extra: updates are collected for {window} and sent as a single message, so a busy afternoon does not become twenty separate mails.',
  'notify.windowSeconds': '{count} seconds',
  'notify.windowMinute': 'a minute',
  'notify.windowMinutes': '{count} minutes',
  'notify.emailAbout': 'Email me about',
  'notify.digest': 'A summary instead of each one',
  'notify.digestHint': 'Collect the inbox into one email rather than sending as things happen. Mentions and assignments still arrive on their own.',
  'notify.digestOff': 'Off',
  'notify.digestDaily': 'Once a day',
  'notify.digestWeekly': 'Once a week',
  'notify.all': 'Everything',
  'notify.allHint': 'Assignments, mentions and comments on work you follow',
  'notify.important': 'Only what needs me',
  'notify.importantHint': 'Assignments and mentions',
  'notify.none': 'Nothing',
  'notify.noneHint': 'In-app inbox only',
  'notify.saved': 'Notification settings saved',
  'notify.delivery': 'Delivery',
  'notify.relay': 'Relay',
  'notify.api': 'Provider',
  'notify.noEncryption': 'not encrypted',
  'notify.sender': 'Sender',
  'notify.queued': 'Waiting in the queue',
  'notify.sendTest': 'Send a test email',
  'notify.testSent': 'Test email sent to {email}',
  'notify.testFailed': 'The relay refused the message',
  'notify.captureTitle': 'This is a capture inbox, not a mail server',
  'notify.captureBody': 'Messages are accepted by {host} and stop there — no recipient ever gets them. Read them in the local inbox (Mailpit, usually on port 8025), and set KOLIBRI_SMTP_URL to a real relay before this instance is used for actual work.',
  'notify.captureChip': 'capture',
  'notify.noRelayTitle': 'No mail relay configured',
  'notify.noRelayHint': 'This instance sends nothing by email — notifications stay in the in-app inbox, which is the source of truth anyway. Set KOLIBRI_SMTP_URL to a relay and restart to turn delivery on.',

  'workspace.name': 'Workspace name',
  'workspace.adminOnly': 'Only admins can rename the workspace.',
  'workspace.updated': 'Workspace updated',
  'workspace.features': 'Features',
  'workspace.featureTime': 'Time tracking',
  'workspace.featureTimeHint': 'Log time on a task and run a timer. Off by default: estimates here are in points, so there is nothing yet to compare the hours against.',
  'workspace.featureBudget': 'Budgets',
  'workspace.featureBudgetHint': 'Plan what things cost, record what has actually gone, and see the two against each other. Infrastructure, investments and suppliers, split across the projects that pay for them.',
  'workspace.featureAi': 'Task reviews and canvas boards',
  'workspace.featureAiHint': 'Adds a button to a task that asks {provider} to read it back and suggest clearer wording, and lets members ask it to draw a board on a canvas or change one. Manual, one request at a time: nothing is sent until somebody asks.',
  'workspace.featureAiUnset': 'No model is configured on this server. Whoever runs it sets a key — see docs/ai.md.',
  /* ------------------------------------------------------------ mailboxes */
  'mailbox.title': 'Connected mailboxes',
  'mailbox.hint': 'Shared inboxes, searchable from one place and from an assistant. Read-only: nothing here sends, replies, deletes or marks as read.',
  'mailbox.address': 'Address',
  'mailbox.add': 'Connect',
  'mailbox.addressInvalid': 'A mailbox is named by an email address.',
  'mailbox.name': 'Name on screen',
  'mailbox.host': 'IMAP server',
  'mailbox.port': 'Port',
  'mailbox.username': 'Username, if not the address',
  'mailbox.encryption': 'Encryption',
  'mailbox.encryptionNone': 'None — local capture only',
  'mailbox.password': 'Password',
  'mailbox.passwordSet': 'Stored — type to replace',
  'mailbox.passwordUnset': 'No password yet',
  'mailbox.passwordStored': 'Password stored.',
  'mailbox.connected': 'Mailbox connected.',
  'mailbox.connectedTo': 'Signed in with {provider}.',
  'mailbox.connectWith': 'Connect with {provider}',
  'mailbox.reconnect': 'Sign in again',
  'mailbox.redirectUri': 'Redirect URI for the app registration: {uri}',
  'mailbox.access': 'Who may read it',
  'mailbox.accessWorkspace': 'Everybody in the workspace',
  'mailbox.accessMembers': 'Only the people named below',
  'mailbox.syncDays': 'Fetch back this many days (0 = everything)',
  'mailbox.test': 'Test connection',
  'mailbox.testOk': 'Signed in and read the inbox.',
  'mailbox.syncNow': 'Sync now',
  'mailbox.synced': '{count} new messages.',
  'mailbox.disconnect': 'Disconnect',
  'mailbox.disconnectHint': 'Disconnecting removes the stored copy of this mailbox — messages, attachment list and search index. The mail itself is untouched on the server.',
  'mailbox.disconnectConfirm': 'Disconnect {address} and delete the copy of its messages?',
  'mailbox.failing': 'Failing',
  'mailbox.never': 'Not synced yet',
  'mailbox.messages_one': '{count} message',
  'mailbox.messages_other': '{count} messages',
  'mail.title': 'Mail',
  'mail.search': 'Search',
  'mail.searchPlaceholder': 'from:stripe since:2024-01 invoice',
  'mail.searchHint': 'Words search the subject, body and attachment names. Prefixes: from, to, subject, since, until, file — and von, an, betreff, seit, bis, anhang.',
  'mail.documents': 'Documents',
  'mail.documentsHint': 'Ranked by how likely each message is to carry an invoice, receipt or statement. A ranking, not a decision.',
  'mail.stats': 'Numbers',
  'mail.results_one': '{count} message',
  'mail.results_other': '{count} messages',
  'mail.searched': 'Searched: {mailboxes}',
  'mail.noMailbox': 'No mailbox is connected that you may read.',
  'mail.attachments_one': '{count} file',
  'mail.attachments_other': '{count} files',
  'mail.thread': 'Whole conversation',
  'mail.coverage': 'Covers {from} to {to}',
  'mail.perMonth': 'Per month',
  'mail.topSenders': 'Who writes most',
  'mail.response': 'Median reply time',
  'mail.responseUnmeasurable': 'Not measurable — no sent mail is being fetched.',
  'mail.minutes': '{count} min',
  'settings.tabMailboxes': 'Mailboxes',
  'workspace.featureMail': 'Connected mailboxes',
  'workspace.featureMailHint': 'Shared inboxes, searchable from one place and from an assistant. Switching it off hides the screens and stops MCP reading; disconnecting a mailbox is what deletes its copy.',
  'workspace.yours': 'Your workspaces',
  'workspace.switch': 'Switch',
  'workspace.new': 'New workspace',
  'workspace.created': 'Workspace created',

  'members.role': 'Role',
  'members.remove': 'Remove {name} from this workspace?',
  'members.invites': 'Invites',
  'members.createInvite': 'Create invite link',
  'members.inviteCopied': 'Invite link copied to clipboard',
  'members.copied': 'Copied',
  'members.roleOwner': 'owner',
  'members.roleAdmin': 'admin',
  'members.roleMember': 'member',
  'members.roleGuest': 'guest',

  'api.intro': 'Kolibri speaks the Model Context Protocol. Point an assistant at this instance and it can read the backlog, file tasks, move them through the workflow and write pages — with the same permissions as the token owner.',
  'api.tokens': 'API tokens',
  'calendar.title': 'Calendar feed',
  'calendar.intro': 'A link your calendar can subscribe to. Everything with a due date that is on you, from every workspace — it updates itself.',
  'calendar.create': 'Create the link',
  'calendar.copied': 'Copied',
  'calendar.paste': 'Paste it into Google Calendar, Apple Calendar, Outlook or Thunderbird as a subscribed calendar. Anyone who has the link can read what is in it, so treat it like a password.',
  'calendar.asTasks': 'Copy as a task list',
  'calendar.rotate': 'New link',
  'calendar.rotateWarning': 'Every calendar already subscribed to the old link will stop updating and has to be re-added. Carry on?',
  'calendar.rotated': 'The old link no longer works',
  'calendar.off': 'Turn off',
  'calendar.offWarning': 'The link stops working and every calendar subscribed to it stops updating. Carry on?',
  'api.tokenName': 'Token name',
  'api.tokenAllWorkspaces': 'no default workspace',
  'api.tokenOtherWorkspace': 'a workspace you are not in',
  'api.tokenConfine': 'Confine this token to {workspace}',
  'api.tokenConfinedTo': 'A confined token reaches {workspace} and nothing else: every other workspace answers as though the account were not in it, and the token cannot create further tokens or a calendar link.',
  'api.tokenScopeConfined': 'confined to {workspace}',
  'api.tokenScopeDefault': 'defaults to {workspace}',
  'api.tokenBoundTo': 'A new token defaults to {workspace}: calls that name no workspace of their own land there. For another default, switch to it and create the token there.',
  'api.noTokens': 'No tokens yet.',
  'api.neverUsed': 'never used',
  'api.usedAgo': 'used {time}',
  'api.revoked': 'Token revoked',
  'api.connect': 'Connect an MCP client',
  'api.copyConfig': 'Copy command',
  'api.configCopied': 'Command copied',
  'api.orDirect': 'A client that speaks only stdio runs the bridge in packages/mcp instead — see docs/mcp.md.',
  'api.onTheWeb': 'On claude.ai, add {url} as a custom connector — there is nothing to paste but the address, and you sign in here when it asks.',
  'api.copyNow': 'Copy your token now',
  'api.copyNowHint': 'This is the only time the token is shown. Store it somewhere safe.',
  'api.copyToken': 'Copy token',
  'api.tokenCopied': 'Token copied',

  'data.offlineCopy': 'Offline copy',
  'data.offlineIntro': 'This device keeps a full copy of the workspace in IndexedDB. Changes you make offline are queued and merged field by field when you come back online, so nobody\'s edits get overwritten wholesale.',
  'data.localData': 'Local data',
  'data.available': 'Available',
  'data.persisted': 'Storage persisted',
  'data.persistedYes': 'Yes',
  'data.persistedBestEffort': 'Best effort',
  'data.checkUpdate': 'Check for app update',
  'data.updateChecked': 'Checked for updates',
  'data.signOutClear': 'Sign out and clear this device',
  'data.cleared': 'Local copy cleared',
  'transfer.intoNewHint': 'A new project is made and every reference in the file is rewritten to point inside it. Nothing that is already here is touched.',
  'transfer.intoThisHint': 'The tasks are added to this project. Its own states, labels and fields are reused where the names match, and a task the file has been imported from before is updated rather than added twice.',
  'transfer.notAWorkspace': 'That file is not a Kolibri workspace export',
  'transfer.exportZip': 'Export as .zip (with files)',
  'transfer.exportCsv': 'Export tasks as CSV',
  'transfer.into': 'Import into',
  'transfer.intoNew': 'A new project',
  'transfer.intoThis': 'This project',
  'transfer.merged': '{count} task(s) updated, {added} added',
  'transfer.missingFiles': 'These files were not in the export and did not come across: {names}',
  'data.exportWorkspace': 'Export this workspace',
  'data.exportWorkspaceHint': 'Every project, the teams, the pages and the saved views, as one file you can read and import anywhere. Not a backup — a backup has to be exact, and that is what the snapshots below are.',
  'data.exportAdminOnly': 'An administrator of this workspace can export the whole of it. You can export any project you can see from its own settings.',
  'data.exportContents': 'In this workspace',
  'data.exportCounts': '{projects} projects · {tasks} tasks · {pages} pages · {people} people',
  'data.exportFiles': 'Uploaded files',
  'data.exportJson': 'Download JSON',
  'data.exportZip': 'Download .zip',
  'data.exportCsv': 'Tasks as CSV',
  'data.exportFormatsHint': 'The JSON is the whole workspace and is readable in a text editor; the .zip is the same document with the uploaded files beside it. The CSV is the task list, in the columns the importer reads back. Everything here is also plain data over the API: any collection returns JSON, and the SQLite file under /data is yours to copy.',
  'data.importWorkspace': 'Import a workspace',
  'data.importWorkspaceHint': 'A .json or .zip exported from this or another Kolibri. It always arrives as a new workspace, so nothing here can be damaged by trying.',
  'data.importChoose': 'Choose a file',
  'data.importAbout': 'Import {name}?',
  'data.importNewWorkspace': 'It will arrive as a new workspace. Nothing that is already here is touched.',
  'data.importPeople': 'People are matched by email address. Anybody without an account here is named in the report and their work arrives unassigned.',
  'data.importConfirm': 'Import',
  'data.importing': 'Importing…',
  'data.workspaceImported': '{name} imported',
  'data.mine': 'Your own data',
  'data.mineHint': 'Everything this instance holds about you: your account, the tasks you are on, what you have written, the time you have logged and the devices you are signed in on. Your passwords, tokens and second factor are not in it — handing those back would make a copy you could not revoke.',
  'data.mineDownload': 'Download my data',
  'restore.action': 'Restore',
  'restore.title': 'Restore this snapshot?',
  'restore.about': 'Put the snapshot from {name} back, over everything in this instance.',
  'restore.aboutFile': 'Restore this instance from {name}. The file is checked before anything here is replaced — a database that is not a Kolibri snapshot is refused rather than half-copied.',
  'restore.upload': 'Restore from a file',
  'restore.uploadHint': 'A snapshot downloaded from another instance — this is how a Kolibri deployed somewhere new becomes the old one. Everything comes across: accounts, workspaces, projects, pages, files and settings.',
  'restore.choose': 'Choose a snapshot',
  'restore.arriving': 'What arrives',
  'restore.going': 'What is here now',
  'restore.goingValue': '{users} users, {workspaces} workspaces, {tasks} tasks',
  'restore.replaces': 'Everything currently in this instance is replaced. Anything created since that snapshot was taken is gone.',
  'restore.emptyInstance': 'Nothing has been done in this instance yet, so there is nothing here to lose.',
  'restore.safety': 'A snapshot of the current state is taken first, so restoring the wrong file is survivable.',
  'restore.noSafety': 'Neither a backup directory nor a bucket is set up, so no copy of the current state can be kept. Back up now first if there is anything here worth keeping.',
  'restore.signedOut': 'Everybody is signed out, including you. Sign in again with the password from the restored instance — that is what makes every device fetch the restored data instead of merging its own copy into it.',
  'restore.secret': 'Existing sessions and API tokens only survive if this instance has the same KOLIBRI_SECRET as the one the snapshot came from. Passwords always work.',
  'restore.typeToConfirm': 'Type {word} to confirm',
  'restore.word': 'restore',
  'restore.confirm': 'Replace everything',
  'restore.working': 'Restoring…',
  'restore.done': 'Restored. Signing you out…',
  'restore.doneKept': 'Restored. What was here is kept as {name}. Signing you out…',
  'backup.title': 'Backups',
  'backup.hint': 'A snapshot is the database copied through SQLite, which is the only copy guaranteed to open. This is the exact one; the export above is the portable one.',
  'backup.off': 'Backups are not scheduled',
  'backup.offHint': 'Say where they go under Server → Backups — a bucket, or an email address — and this instance takes one every night. Or set KOLIBRI_BACKUP_DIR to a directory on a volume other than the database’s.',
  'backup.schedule': 'Schedule',
  'backup.scheduleValue': 'Daily at {hour}:00, keeping {keep}',
  'backup.keepAll': 'all',
  'backup.where': 'Written to',
  'backup.last': 'Last one',
  'backup.never': 'None yet',
  'backup.offsite': 'Copied off this machine',
  'backup.offsiteOn': 'To the object store',
  'backup.offsiteOff': 'No — this disk only',
  'backup.now': 'Back up now',
  'backup.taking': 'Taking a snapshot…',
  'backup.taken': 'Snapshot taken and checked',
  'backup.none': 'Nothing yet. Take one now, or wait for tonight.',
  'backup.intact': 'Opens',
  'backup.damaged': 'Damaged',
  'backup.verify': 'Check',
  'backup.verified': 'It opens and passes an integrity check',
  'backup.download': 'Download',
  'backup.deleteConfirm': 'Delete the snapshot from {name}? This cannot be undone.',
  'backup.deleted': 'Snapshot deleted',
  'backup.restoreHint': 'Restoring replaces everything in this instance and keeps a copy of what was here first, in the directory or the bucket. For an instance that will not start, kolibri restore <snapshot> --force does it from the command line, against a stopped server.',
  'backup.scheduleDaily': 'Daily at {hour}:00',
  'backup.bucket': 'Bucket',
  'backup.email': 'Email',
  'backup.notYet': 'nothing sent yet',
  'backup.arrived': '{name} arrived {when}',
  'backup.failedAt': 'failed {when}: {detail}',
  'backup.lastArrived': 'the last one that arrived: {when}',
  'backup.neverArrived': 'nothing has arrived there yet',
  'backup.inBucket': 'In the bucket — {where}',
  'backup.bucketEmpty': 'Nothing in the bucket yet. Tonight puts one there, or Back up now does.',
  'backup.sendFailed': 'Taken and checked — not sent ({place}): {detail}',
  'backup.takenAndSent': 'Snapshot taken, checked and sent: {places}',
  'backup.sent': 'Snapshot taken, checked and sent: {places} — nothing is kept on this machine',
  'restore.aboutBucket': 'Restore {name} from the bucket, over everything in this instance. It is fetched and checked before anything here is replaced, and the files come out of the bucket too.',
  'restore.doneKeptBucket': 'Restored. What was here is in the bucket as {name}. Signing you out…',
  'data.storageUnavailable': 'Storage information unavailable',
  'data.storageUnavailableHint': 'Your browser does not expose quota details.',

  /* ------------------------------------------------------------- editor */
  'editor.bold': 'Bold',
  'editor.italic': 'Italic',
  'editor.code': 'Code',
  'editor.heading': 'Heading',
  'editor.bulletList': 'Bullet list',
  'editor.checklist': 'Checklist',
  'editor.quote': 'Quote',
  'editor.attachImage': 'Attach image',
  'editor.formatting': 'Formatting',
  'editor.preview': 'Preview',
  'editor.write': 'Write',
  'editor.uploading': 'Uploading…',
  'editor.placeholder': 'Write in markdown…',
  'editor.mentionPeople': 'People to mention',
  'editor.mentionWork': 'Projects and tasks',
  'editor.mentionPage': 'Pages to link to',
  'editor.nothingToPreview': 'Nothing to preview yet.',
  'editor.uploadFailed': 'Upload failed',
  'editor.uploadFailedReason': 'Upload failed: {reason}',
  'editor.commands': 'Blocks',
  'editor.cmdHint': 'Write in markdown — / for a block, [[ for a page, @ for a person',
  'editor.cmdHeading': 'Heading',
  'editor.cmdHeadingHint': '## Section',
  'editor.cmdSubheading': 'Subheading',
  'editor.cmdSubheadingHint': '### Smaller section',
  'editor.cmdListHint': 'One thing per line',
  'editor.cmdNumbered': 'Numbered list',
  'editor.cmdNumberedHint': 'Steps in order',
  'editor.cmdTodoHint': 'Boxes you can tick',
  'editor.cmdQuoteHint': 'Somebody else’s words',
  'editor.cmdCode': 'Code block',
  'editor.cmdCodeHint': 'Fenced, not indented',
  'editor.cmdTable': 'Table',
  'editor.cmdTableHint': 'Two columns to start',
  'editor.cmdDivider': 'Divider',
  'editor.cmdBar': 'Progress bar',
  'editor.cmdBarHint': 'A share, as a bar: {48%} or {3/8}',
  'editor.cmdDividerHint': 'A line across',
  'editor.cmdDiagram': 'Diagram',
  'editor.cmdDiagramHint': 'Mermaid, drawn as you write',
  'editor.cmdEmbed': 'Embed a page',
  'editor.cmdEmbedHint': 'Draw another page here',
  'editor.strike': 'Strikethrough',
  'editor.link': 'Link',
  'editor.cmdParagraph': 'Paragraph',
  'editor.clearFormatting': 'Clear formatting',
  'editor.visual': 'Visual',
  'editor.source': 'HTML',
  'editor.tidy': 'Tidy the markup',
  'editor.htmlPlaceholder': '<h2>A section</h2>\n<p>…</p>',
  'editor.placeholderHtml': 'Write here. Paste from anywhere — it is cleaned on the way in.',
  'editor.htmlHint': 'Only a fixed set of tags survives: no scripts, no styles, no page-wide classes. Tidy re-indents and drops the rest.',
  'editor.visualHint': 'Ctrl/Cmd+B and +I work as usual; a pasted document keeps its headings, lists and tables.',


  /* --------------------------------------------------------------- guide */
  'guide.title': 'Guide',
  'guide.tabOverview': 'Overview',
  'guide.tabHierarchy': 'Hierarchy',
  'guide.tabFeatures': 'Features',
  'guide.tabShortcuts': 'Shortcuts',
  'guide.steps': 'Steps',
  'guide.play': 'Play',
  'guide.pause': 'Pause',
  'guide.previousStep': 'Previous step',
  'guide.nextStep': 'Next step',

  'guide.intro': 'Kolibri keeps projects, tasks and pages in one place — on your own server, in your browser, and available to an assistant. This guide explains what each part is for, how the parts fit inside each other, and how to get anything done in as few clicks as possible.',
  'guide.principleOfflineTitle': 'Never waits for the network',
  'guide.principleOfflineBody': 'Every screen reads from a copy of the workspace on this device. Changes queue up and merge field by field when you are back.',
  'guide.principleHostTitle': 'Yours to host',
  'guide.principleHostBody': 'One command brings up the whole stack. The data is one SQLite file and a folder of uploads you can copy.',
  'guide.principleAssistantTitle': 'An assistant can join in',
  'guide.principleAssistantBody': 'Kolibri speaks the Model Context Protocol, so an AI can read the backlog and file work with exactly the permissions you grant.',

  'guide.bigPicture': 'How the pieces fit',
  'guide.bigPictureLead': 'Everything nests. The animation builds the picture one layer at a time — the same layers you can click through under Hierarchy.',
  'guide.firstFiveTitle': 'Your first five minutes',
  'guide.firstFive1': 'Create a project. It gets a key like WEB, a workflow, and a set of labels.',
  'guide.firstFive2': 'Press C anywhere and write down three things that need doing.',
  'guide.firstFive3': 'Switch the project to the board and drag one of them into progress.',
  'guide.firstFive4': 'Write a page for the decisions nobody should have to ask about twice.',
  'guide.firstFive5': 'Invite the rest of the team from Settings, then close signup.',
  'guide.startHere': 'Create a project',

  'guide.overview.label': 'How a workspace is built up',
  'guide.overview.s0': 'A workspace is the outer boundary. People, projects and pages belong to exactly one — nothing is shared across two.',
  'guide.overview.s1': 'A project holds the actual work. It brings its own key, its own workflow states and its own labels.',
  'guide.overview.s2': 'Tasks live inside the project and take its key as their number, so WEB-1 stays WEB-1 forever.',
  'guide.overview.s3': 'Cycles say when something happens, modules say what it adds up to. Both are optional.',
  'guide.overview.s4': 'Pages hold the writing: specs, decisions, handbooks — either in a project or across the workspace.',
  'guide.overview.s5': 'All of it lives on your device as well as on the server, syncs both ways, and is readable by an assistant.',
  'guide.overview.workspace': 'Workspace',
  'guide.overview.project': 'Project',
  'guide.overview.cycle': 'Cycle',
  'guide.overview.module': 'Module',
  'guide.overview.page': 'Page',
  'guide.overview.device': 'Your device',
  'guide.overview.sync': 'Sync',
  'guide.overview.assistant': 'Assistant',

  'guide.sample.task1': 'Redesign the pricing page',
  'guide.sample.task2': 'Ship dark mode',
  'guide.sample.task3': 'Add customer logos',
  'guide.sample.newTask': 'Write the launch email',
  'guide.sample.project': 'Website',
  'guide.sample.person1': 'Ada',
  'guide.sample.person2': 'Lin',
  'guide.sample.page1': 'Team handbook',
  'guide.sample.page2': 'Onboarding',
  'guide.sample.page3': 'Decisions',

  'guide.featuresIntro': 'One card per feature: what it is for, a short animation of it working, and the steps to do it yourself. Every card ends with a link into the real screen.',

  'guide.capture.title': 'Capturing a task',
  'guide.capture.lead': 'The fastest path from a thought to a tracked piece of work. Only the title is required — everything else can be filled in later, or never.',
  'guide.capture.cta': 'Try it on my work',
  'guide.capture.label': 'Creating a task with quick add',
  'guide.capture.s0': 'Press C on any screen, or use the button in the sidebar. You do not have to be in a project first.',
  'guide.capture.s1': 'Write the title. This is the only thing that is required.',
  'guide.capture.s2': 'Optionally pick a project, a priority and who is doing it. The last project you used is preselected.',
  'guide.capture.s3': 'The task appears immediately with its own number — the server hands out the next one without gaps.',
  'guide.capture.h1': 'Press C, or tap the plus button on the tab bar.',
  'guide.capture.h2': 'Type the title and press Cmd-Enter to create it in one go.',
  'guide.capture.h3': 'Use Save and new to write down several things without leaving the form.',
  'guide.capture.h4': 'Open the task later to add a description, files, sub-tasks and links.',
  'guide.capture.h5': 'Coming from somewhere else? Project → Settings → Import reads a CSV, shows what it understood, and only then writes.',

  'guide.views.title': 'Views: list, board, table, calendar',
  'guide.views.lead': 'The same tasks, arranged the way the question needs. Layout, grouping and filters are remembered per project, so a board stays a board when you come back.',
  'guide.views.cta': 'Open a project',
  'guide.views.label': 'Switching layout, grouping and moving a card',
  'guide.views.s0': 'The list groups by workflow state and reads top to bottom.',
  'guide.views.s1': 'Group by assignee instead and the same tasks regroup under the people doing them.',
  'guide.views.s2': 'Switch to the board and each state becomes a column.',
  'guide.views.s3': 'Drag a card to the next column — that is the same thing as changing its state.',
  'guide.views.s4': 'On a phone there is nothing to drag: the card menu offers the same move.',
  'guide.views.s5': 'A card in a done column is finished, and the completion time is stamped for you.',
  'guide.views.h1': 'Use the three icons at the top left to switch between list, board and calendar.',
  'guide.views.h2': 'Display sets grouping and sort order; Filter narrows by state, priority, assignee or label.',
  'guide.views.h3': 'On the board, drag a card between columns — or use the dots menu on touch.',
  'guide.views.h4': 'Your choices are stored per project on this device, so each project can look different.',
  'guide.views.h5': 'Saved views give a set of choices a name. Save this view… keeps it, and a dot next to the name means you have changed something since.',
  'guide.views.h6': 'The table compares rather than reads: one row per task, sortable headers, and the columns that only matter side by side are dropped on a phone.',
  'guide.views.h7': 'Tick the box in front of a row to select it, shift-click for a range, long press on a touch screen. What you can do to several at once appears at the bottom.',
  'guide.views.h8': 'Group or filter by label — bug, feature, whatever the team says. Each project keeps its own list under Settings.',

  'guide.planning.title': 'Cycles and modules',
  'guide.planning.lead': 'Two different questions. A cycle is a window of time — this fortnight. A module is an outcome that takes several of them — the checkout rewrite.',
  'guide.planning.cta': 'Plan a project',
  'guide.planning.label': 'Planning with cycles and modules',
  'guide.planning.s0': 'A cycle is a fixed window: a start date, an end date, and whatever you decide to put in it.',
  'guide.planning.s1': 'Several cycles in a row are your rhythm. Nothing expires — unfinished work moves on to the next one.',
  'guide.planning.s2': 'A module runs across cycles and stands for the outcome rather than the fortnight.',
  'guide.planning.s3': 'Progress is counted both ways: finished tasks, and finished estimate points.',
  'guide.planning.cycleName': 'Cycle {number}',
  'guide.planning.moduleName': 'Checkout rewrite',
  'guide.planning.h1': 'Open a project and switch to the Cycles tab to create one.',
  'guide.planning.h2': 'Assign tasks to it from the task itself, or create them straight inside the cycle.',
  'guide.planning.h3': 'Use Modules for milestones that outlive a single cycle.',
  'guide.planning.h4': 'Give tasks an estimate if you want the points progress to mean anything.',
  'guide.planning.h5': 'Estimates are points — a guess at size. Time is measured separately on each task: start the clock, or log what it took afterwards.',
  'guide.planning.h6': 'The Insights tab counts what the tasks already say: how much gets finished per week, a burn-up over the cycle, and how long things typically take.',

  'guide.sync.title': 'Offline and sync',
  'guide.sync.lead': 'The part worth understanding once. Kolibri does not lock a task while somebody edits it — instead, it remembers who changed which field last, so two people working at the same time both keep their change.',
  'guide.sync.cta': 'See the local copy',
  'guide.sync.label': 'Two people editing one task, one of them offline',
  'guide.sync.s0': 'Ada and Lin are both looking at the same task. Everything is in step.',
  'guide.sync.s1': 'Ada loses the network on a train and rewrites the title anyway. The change waits in a queue on her device.',
  'guide.sync.s2': 'Lin is online and raises the priority. The server takes it straight away.',
  'guide.sync.s3': 'Ada gets signal back and her queue empties on its own — nothing to press.',
  'guide.sync.s4': 'The server merges field by field: the title from Ada, the priority from Lin. Neither edit overwrites the other.',
  'guide.sync.s5': 'Both devices end up with the same row, and the queue is empty again.',
  'guide.sync.titleOld': 'Pricing page',
  'guide.sync.titleNew': 'Pricing page v2',
  'guide.sync.outbox': 'Queue {count}',
  'guide.sync.server': 'Server',
  'guide.sync.fieldTitle': 'title',
  'guide.sync.fieldPriority': 'priority',
  'guide.sync.h1': 'Nothing to turn on: every screen already reads from the copy on this device.',
  'guide.sync.h2': 'The pill in the header is the truth — Synced, Syncing, or Offline with the number waiting.',
  'guide.sync.h3': 'Two people can edit the same task at once as long as they touch different fields.',
  'guide.sync.h4': 'Install it as an app from the browser menu and it opens without a connection at all.',
  'guide.sync.h5': 'A delete keeps the row and marks it, which is how two devices agree it is gone. Settings → Data lists what was deleted or archived, and brings it back.',

  'guide.pages.title': 'Pages',
  'guide.pages.lead': 'A markdown wiki that keeps its history. Good for the things a task is too small for: specs, decisions, onboarding, meeting notes.',
  'guide.pages.cta': 'Open pages',
  'guide.pages.label': 'Writing, nesting and restoring a page',
  'guide.pages.s0': 'A page is a markdown document with a title, an icon and a body.',
  'guide.pages.s1': 'Pages nest as deep as you like, so a handbook can have chapters.',
  'guide.pages.s2': 'Drop an image straight into the text; it is shrunk in the browser before it is uploaded.',
  'guide.pages.s3': 'Every edit writes a version, and any version can be restored — the current text is kept.',
  'guide.pages.versions': 'Version history',
  'guide.pages.h1': 'Create a page from the Pages screen, or inside a project on its Pages tab.',
  'guide.pages.h2': 'Press Edit to write; text saves itself while you type.',
  'guide.pages.h3': 'Use Add sub-page to nest, and Move to project to change where a page lives.',
  'guide.pages.h4': 'Version history is in the dots menu, next to the copy link.',
  'guide.pages.h5': 'Under every page is a discussion. Write @ and a name to pull somebody into it — whoever wrote the page, and whoever has already said something, hear about a new comment.',
  'guide.pages.h6': 'The page menu carries the rest: labels, watching it, who can see it, keeping it as a template, what changed since any version, and downloading it as markdown.',

  'guide.collab.title': 'Comments, mentions and the inbox',
  'guide.chat.title': 'Chat: channels and direct messages',
  'guide.chat.lead': 'Conversation that is not about one task. Built out of the same rows as everything else, so it sends from a train and arrives when the tunnel ends.',
  'guide.chat.cta': 'Open chat',
  'guide.chat.label': 'Two people, no signal, one conversation',
  'guide.chat.s0': 'Ada and Lin both open a conversation with each other. Neither has signal.',
  'guide.chat.s1': 'Neither device invented an id — both worked out the same one from who is in the conversation.',
  'guide.chat.s2': 'The tunnel ends and both devices send what they were holding.',
  'guide.chat.s3': 'One conversation, both lines in it. Not two rooms with half the history each.',
  'guide.chat.offline': 'no signal',
  'guide.chat.said1': 'Are you around this afternoon?',
  'guide.chat.said2': 'Just leaving now — call me?',
  'guide.chat.derivedId': 'dm.ada.lin',
  'guide.chat.oneRoom': 'One conversation',
  'guide.chat.bothLines': 'both lines',
  'guide.chat.h1': 'A channel is for the team; a direct message is between the two of you. Three or more people is a private channel with a name.',
  'guide.chat.h2': 'A channel tells you when you are named. Turn that up to every message, or off, per conversation.',
  'guide.chat.h3': 'Paste or drop a screenshot straight into the box; react with an emoji instead of writing "ok".',
  'guide.chat.h4': 'A private channel keeps a member list, and you choose whether anybody in it can invite or only you and the admins.',
  'guide.collab.lead': 'Discussion sits with the work rather than in a chat somewhere else. Mentioning somebody is what reaches them; everything else is there to read when they look.',
  'guide.collab.cta': 'Open the inbox',
  'guide.collab.label': 'From a comment to a notification to one email',
  'guide.collab.s0': 'Ada writes a comment on the task and mentions Lin by name.',
  'guide.collab.s1': 'Lin gets a notification in her inbox — written in her language, not in the language Ada was using.',
  'guide.collab.s2': 'Notifications are collected for a couple of minutes and then sent as one email, not five.',
  'guide.collab.s3': 'Lin decides how much reaches her by email; the in-app inbox always has everything.',
  'guide.collab.commentBody': 'Can you take a look at this,',
  'guide.collab.notification': 'You were mentioned in WEB-1',
  'guide.collab.inRecipientLanguage': 'in the language the recipient chose',
  'guide.collab.oneMail': 'One summary email',
  'guide.collab.preference': 'your choice',
  'guide.collab.h1': 'Write comments in markdown; Cmd-Enter sends.',
  'guide.collab.h2': 'Mention people with an at sign — a first name, a display name or an email address all work.',
  'guide.collab.h3': 'The inbox shows unread first; Mark all read clears it.',
  'guide.collab.h4': 'Settings, Notifications decides what is worth an email. Everything is in the inbox either way.',
  'guide.collab.h5': 'Write # to point at a project or a task — or just type WEB-42, which becomes a link on its own. Works in chat, in comments and on pages.',
  'guide.collab.h6': 'Enter continues a list — another bullet, the next number, another checkbox — and a second Enter ends it. Tab nests. A checkbox can be ticked in the preview and in a task description, without opening an editor.',

  'guide.teams.title': 'Teams, invites and roles',
  'guide.teams.lead': 'Teams group people and the projects they own — useful once more than a handful of people share the workspace. Roles decide what somebody can change.',
  'guide.teams.cta': 'Open teams',
  'guide.teams.h1': 'Invite people from Settings, Members with a link — one link, any number of people.',
  'guide.teams.h2': 'Turn signup off once everybody is in; invites keep working.',
  'guide.teams.h3': 'Create a team and attach the projects it owns.',
  'guide.teams.h4': 'Make a project private to keep it to the people you invite to it.',
  'guide.role.owner': 'Owns the instance. Everything an admin can do, and cannot be removed by one.',
  'guide.role.admin': 'Manages members, invites and workspace settings.',
  'guide.role.member': 'The normal role: creates and edits work everywhere they can see.',
  'guide.role.guest': 'Reads, but every write is refused by the server.',

  'guide.assistant.title': 'Assistants and the API',
  'guide.assistant.lead': 'Kolibri speaks the Model Context Protocol natively, so an assistant is a user with a token rather than a plugin. What the token may do is what it does.',
  'guide.assistant.cta': 'Create a token',
  'guide.assistant.label': 'An assistant reading and writing through a scoped token',
  'guide.assistant.s0': 'Create a token in Settings and give it a scope: read, or read and write.',
  'guide.assistant.s1': 'Point an assistant at the instance with that token. It can list projects, tasks, cycles and pages.',
  'guide.assistant.s2': 'A read-only token is refused for every write — no matter how the request is phrased.',
  'guide.assistant.s3': 'Give it write scope and the same assistant can file tasks, move them and write pages.',
  'guide.assistant.agent': 'Assistant',
  'guide.assistant.scopeRead': 'read',
  'guide.assistant.scopeWrite': 'read and write',
  'guide.assistant.createdTask': 'Filed by the assistant',
  'guide.assistant.h1': 'Settings, API and MCP: name a token and create it. It is shown once.',
  'guide.assistant.h2': 'Copy the ready-made config into your MCP client, or point it at the URL directly.',
  'guide.assistant.h3': 'Hand out a read token when you only want the backlog looked at, not changed.',
  'guide.assistant.h4': 'Revoke a token at any time; anything it created stays.',

  'guide.hierarchyIntro': 'Most questions about a tool like this are really questions about containment: where does a cycle live, can a task be in two of them, what happens to sub-tasks when a project goes. Pick anything in the tree to see what holds it, what it holds, and the one rule worth knowing.',
  'guide.hierarchy.outside': 'Outside the tree',
  'guide.hierarchy.livesIn': 'Lives in',
  'guide.hierarchy.contains': 'Contains',
  'guide.hierarchy.nothing': 'Nothing — this is a leaf.',
  'guide.hierarchy.aPerson': 'A person — not the workspace',
  'guide.hierarchy.aProject': 'A project — each has its own list',
  'guide.hierarchy.aTask': 'A task — and the person who logged it',
  'guide.hierarchy.topLevel': 'Nothing — this is the top.',

  'guide.node.workspace': 'Workspace',
  'guide.node.workspaceWhat': 'The outer boundary. People, projects, pages and teams belong to exactly one workspace, and nothing is shared between two of them.',
  'guide.node.workspaceRule': 'You can belong to several and switch at the top left — but data never moves across, not even by copying a link.',
  'guide.node.member': 'Member',
  'guide.node.memberWhat': 'A person with a role in this workspace. Membership is what makes projects, tasks and pages visible at all.',
  'guide.node.memberRule': 'Four roles: owner, admin, member, guest. A guest may read; the server refuses every write it makes.',
  'guide.node.team': 'Team',
  'guide.node.teamWhat': 'Groups people and the projects they own. Worth creating once more than a handful of people share the workspace.',
  'guide.node.teamRule': 'Entirely optional. A project belongs to at most one team; a person can be in as many as you like.',
  'guide.node.project': 'Project',
  'guide.node.projectWhat': 'Where work actually happens. A project brings its own key, its own workflow states and its own labels, so two projects need not look alike.',
  'guide.node.projectRule': 'Public or private. A private project is invisible to everyone who is not invited to it — in the API and in sync, not just in the interface.',
  'guide.node.state': 'Workflow state',
  'guide.node.stateWhat': 'One step of the workflow, and one column on the board. Name them whatever your team says out loud.',
  'guide.node.stateRule': 'Every state sits in one of five groups — backlog, todo, in progress, done, cancelled — and the group is what decides whether a task counts as finished.',
  'guide.node.label': 'Label',
  'guide.node.labelWhat': 'A free tag with a colour, for the cuts that are not a workflow: bug, documentation, needs design.',
  'guide.node.labelRule': 'A task can carry any number of labels. A label without a project applies across the whole workspace.',
  'guide.node.cycle': 'Cycle',
  'guide.node.cycleWhat': 'A window of time with a start and an end — a sprint, an iteration, a fortnight. It answers when, not what.',
  'guide.node.cycleRule': 'A task is in at most one cycle. Nothing expires when a cycle ends: unfinished work simply moves on.',
  'guide.node.module': 'Module',
  'guide.node.moduleWhat': 'An outcome that takes more than one cycle — a milestone. It answers what, not when.',
  'guide.node.moduleRule': 'A task is in at most one module. A module has a target date but no fixed start and end the way a cycle does.',
  'guide.node.task': 'Task',
  'guide.node.taskWhat': 'The unit of work. Title, description, state, priority, assignees, labels, estimate, dates — all optional except the title.',
  'guide.node.taskRule': 'A task lives in exactly one project and carries that project key as its number. The number is handed out once, without gaps, and never changes.',
  'guide.node.subtask': 'Sub-task',
  'guide.node.subtaskWhat': 'A task with a parent. Use it to break something down without losing the thread back to the whole.',
  'guide.node.subtaskRule': 'Technically an ordinary task: its own number, its own assignees, and it can have sub-tasks of its own.',
  'guide.node.relation': 'Relation',
  'guide.node.relationWhat': 'A link between two tasks that is not containment: this blocks that, these two duplicate each other.',
  'guide.node.relationRule': 'Blocks, blocked by, relates to, duplicates. The other side is shown automatically — you only ever link once.',
  'guide.node.comment': 'Comment',
  'guide.node.commentWhat': 'Markdown discussion attached to a task or a page, with files if you need them.',
  'guide.node.commentRule': 'Mentioning somebody notifies them, in their own language rather than in the language you were writing.',
  'guide.node.file': 'File',
  'guide.node.fileWhat': 'An attachment on a task, a page or a comment. Images can be dropped straight into the text.',
  'guide.node.fileRule': 'Content-addressed: uploading the same file twice takes the space once. Images are shrunk in the browser before they go up.',
  'guide.node.projectPage': 'Project page',
  'guide.node.projectPageWhat': 'A page that belongs to one project — the spec, the runbook, the decisions behind the work in it.',
  'guide.node.projectPageRule': 'The same thing as a workspace page, just scoped. Move it between the two whenever it outgrows the project.',
  'guide.node.page': 'Page',
  'guide.node.channel': 'Conversation',
  'guide.node.channelWhat': 'A channel the team can see, or the direct one between two people. Optionally tied to a project, and then only visible to people who can see it.',
  'guide.node.channelRule': 'A direct conversation has no id of its own — it is derived from the two people in it, which is why two devices opening one offline end up in the same room.',
  'guide.node.message': 'Message',
  'guide.node.messageWhat': 'One thing somebody said, in markdown, with the images pasted into it.',
  'guide.node.messageRule': 'Only its author can edit it, and it can never change conversation. Anybody in the room can react to it — that is your name beside their words, not a change to them.',
  'guide.node.channelRead': 'Read marker',
  'guide.node.channelReadWhat': 'How far you have read one conversation, and how much of it you want told to you.',
  'guide.node.channelReadRule': 'Private to you and never shared, which is also why there are no read receipts. One row per person per conversation, so two of your devices converge instead of racing.',
  'guide.node.pageWhat': 'A markdown document with a version history, at workspace level rather than inside a project.',
  'guide.node.pageRule': 'Pages nest as deep as you like. Every change to the body writes a version, and any version can be restored.',
  'guide.node.notification': 'Notification',
  'guide.node.notificationWhat': 'One line in your inbox: an assignment, a mention, a comment on something you follow.',
  'guide.node.notificationRule': 'Belongs to exactly one person and is never shared with the workspace. Email is an optional extra on top of it.',
  'guide.node.time': 'Time entry',
  'guide.node.timeWhat': 'A stretch of work on a task: how long, which day, and a note about what it went on.',
  'guide.node.timeRule': 'A running timer is an entry with a start and no minutes yet, which is why it survives a reload and a second device. One clock at a time per person — starting a second stops the first.',
  'guide.node.view': 'Saved view',
  'guide.node.viewWhat': 'A filter, a grouping and a layout under a name, so a question you ask often is one click away.',
  'guide.node.viewRule': 'Saved on a project it belongs to that project; saved on My work it follows you everywhere. Shared by default, and the one you had open is remembered per device.',
  'guide.node.token': 'API token',
  'guide.node.tokenWhat': 'How an assistant or a script gets in. The same permissions as the person who created it, narrowed by its scope.',
  'guide.node.tokenRule': 'Belongs to one person, can be limited to a single workspace, and a read token is refused for every write.',

  'guide.rulesTitle': 'Rules worth knowing',
  'guide.rulesLead': 'Seven things that explain most of the surprises.',
  'guide.rule1': 'A task lives in exactly one project. Its number comes from the project key, is handed out without gaps, and never changes.',
  'guide.rule2': 'A task is in at most one cycle and at most one module — but it can be in both at once, because they answer different questions.',
  'guide.rule3': 'Cycles and modules belong to a project, so a task can only join the ones from its own project.',
  'guide.rule4': 'Sub-tasks are ordinary tasks with a parent. They have their own number and can be nested further.',
  'guide.rule5': 'Workflow states and labels are per project, so two projects can have completely different columns.',
  'guide.rule6': 'Pages nest as deep as you like and sit either inside a project or across the workspace.',
  'guide.rule7': 'Deleting is soft everywhere: rows stay in the database, and a private project stays invisible to non-members in the API and in sync alike.',

  'guide.shortcutsIntro': 'Everything here works from anywhere in the app, unless you are typing in a field.',
  'guide.shortcutsMac': 'Cmd is Ctrl on Windows and Linux.',
  'guide.key.palette': 'Search everything and jump to it',
  'guide.key.newTask': 'New task',
  'guide.key.guide': 'Open this guide',
  'guide.key.close': 'Close the sheet, menu or palette',
  'guide.key.submit': 'Create the task, or send the comment',
  'guide.key.navigate': 'Move through the search results',
  'guide.key.indent': 'Indent by two spaces in the editor',

  /* ------------------------------------------------- tour and checklist */
  'guide.explainThis': 'How this works',
  'guide.restartTour': 'Run the setup tour again',
  'guide.showChecklist': 'Show the setup checklist again',
  'guide.helpFromHere': 'You can reach this from any empty screen, and from anywhere with ?.',

  'tour.title': 'Welcome to Kolibri',
  'tour.stepOf': 'Step {current} of {total}',
  'tour.back': 'Back',
  'tour.skip': 'Skip',
  'tour.next': 'Next',
  'tour.finish': 'Done',
  'tour.welcomeTitle': 'This instance is yours',
  'tour.welcomeBody': 'Projects, tasks and pages, on your own server. A few steps and you are set up — everything here can be changed later, and you can skip out at any point.',
  'tour.prefsTitle': 'Language and appearance',
  'tour.prefsBody': 'The language applies to this browser and to the emails you receive. Appearance follows your system unless you pin it.',
  'tour.projectTitle': 'A project for the real work',
  'tour.projectBody': 'There is already a starter project to look around in. Add one for something you are actually doing: the name becomes a key — Website relaunch becomes WEB, and its tasks become WEB-1, WEB-2 — along with a workflow and labels you can rename.',
  'tour.projectDone': '“{name}” is ready, with its workflow and labels.',
  'tour.inviteTitle': 'Bring the others in',
  'tour.inviteBody': 'One link, any number of people. You can create more later, and turn signup off once everybody has an account.',
  'tour.inviteReady': 'Link copied. Anyone who opens it can join this workspace.',
  'tour.doneTitle': 'That is the setup',
  'tour.doneBody': 'The rest is explained in the guide: what every feature does, how the pieces nest inside each other, and the shortcuts. There is also a short checklist on your work screen with whatever is still open.',
  'tour.openGuide': 'Open the guide',

  'setup.title': 'Getting set up',
  'setup.progress': '{done} of {total}',
  'setup.hide': 'Hide',
  'setup.project': 'Create a project',
  'setup.projectHint': 'It brings a key, a workflow and a set of labels.',
  'setup.projectCta': 'Create',
  'setup.task': 'Capture a task',
  'setup.taskHint': 'Press C anywhere. Only the title is required.',
  'setup.taskCta': 'New task',
  'setup.page': 'Write a page',
  'setup.pageHint': 'For the decisions nobody should have to ask about twice.',
  'setup.pageCta': 'Open pages',
  'setup.invite': 'Invite the team',
  'setup.inviteHint': 'One link works for any number of people.',
  'setup.inviteCta': 'Members',
  'setup.signup': 'Close signup',
  'setup.signupHint': 'Once everybody has an account, invites still work.',
  'setup.signupCta': 'Settings',


  /* -------------------------------------------- templates + automations */
  'settings.tabAutomation': 'Templates & rules',

  'tpl.one': 'Template',
  'tpl.title': 'Templates',
  'tpl.lead': 'A task, written once. Use one by hand from here or from the quick-add sheet, and point a rule at it to have it filed for you.',
  'tpl.new': 'New template',
  'tpl.edit': 'Edit template',
  'tpl.empty': 'No templates yet',
  'tpl.emptyHint': 'A template is the text a rule files, and a one-click task the rest of the time.',
  'tpl.kind': 'Kind',
  'tpl.kindFeedback': 'Feedback',
  'tpl.kindReview': 'Review',
  'tpl.kindTask': 'Task',
  'tpl.kindBug': 'Bug',
  'tpl.kindChecklist': 'Checklist',
  'tpl.scope': 'Applies to',
  'tpl.scopeWorkspace': 'The whole workspace',
  'tpl.taskTitle': 'Title of the task it creates',
  'tpl.placeholderHelp': 'You can use {identifier}, {title}, {project}, {actor}, {state} and {url} — they are filled in from the task that triggered the rule.',
  'tpl.body': 'Description',
  'tpl.subtasks': 'Checklist',
  'tpl.subtasksHint': 'One sub-task per line. They are created with the task.',
  'tpl.dueInDays': 'Due in days',
  'tpl.dueInDaysHint': 'Counted from the moment it is created. Leave empty for no due date.',
  'tpl.use': 'Use',
  'tpl.used': '{identifier} created',
  'tpl.subtaskCount_one': '{count} step',
  'tpl.subtaskCount_other': '{count} steps',
  'tpl.deleteConfirm': 'Delete the template “{name}”? Rules pointing at it will stop filing anything.',
  'tpl.pick': 'From template',
  'tpl.pickNone': 'No template',
  'tpl.pickEmpty': 'No templates for this project yet',

  'auto.intro': 'A rule watches for one thing happening to a task and files another task from a template. The point is that it is the same every time and nobody has to remember it.',
  'auto.title': 'Rules',
  'auto.lead': 'Scoped to one project, or to the whole workspace so every project behaves the same.',
  'auto.needTemplate': 'Write a template first — a rule needs something to file.',
  'auto.new': 'New rule',
  'auto.edit': 'Edit rule',
  'auto.empty': 'No rules yet',
  'auto.emptyHint': 'The usual one: when a task enters review, ask somebody for feedback.',
  'auto.enabled': 'On',
  'auto.event': 'Event',
  'auto.trigger': 'When',
  'auto.triggerStateEntered': 'a task enters a state',
  'auto.triggerGroupEntered': 'a task enters a state group',
  'auto.triggerCreated': 'a task is created',
  'auto.triggerDueIn': 'A task is due soon',
  'auto.triggerPageChanged': 'A page is edited',
  'auto.triggerCommentAdded': 'Somebody comments on a task',
  'auto.daysBefore': 'Days before',
  'auto.daysBeforeHint': 'Swept once a day. 0 means on the day itself.',
  'auto.action': 'What it does',
  'auto.actionFileTemplate': 'File a task from a template',
  'auto.actionSetFields': 'Change the task it watched',
  'auto.actionLeave': 'Leave as it is',
  'auto.actionAddLabel': 'Add a label',
  'auto.actionAssign': 'Assign to',
  'auto.actionDueIn': 'Due in (days)',
  'auto.actionSetFieldsHint': 'Anything left blank is left alone. Never the state: a rule that moves a task can trigger a rule that moves it back, and two rules editing one row is a merge problem rather than a feature.',
  'auto.state': 'State',
  'auto.stateNeedsProject': 'States belong to a project, so pick one above.',
  'auto.group': 'Group',
  'auto.then': 'File this',
  'auto.recipients': 'Give it to',
  'auto.recipientsHint': 'Several at once is fine, and the list is de-duplicated. Anyone who cannot see the project is left out.',
  'auto.addRecipient': 'Add somebody',
  'auto.recipientUser': 'A named person',
  'auto.recipientAssignees': 'Whoever is on the task',
  'auto.recipientCreator': 'Whoever created the task',
  'auto.recipientActor': 'Whoever triggered it',
  'auto.recipientLead': 'The project lead',
  'auto.recipientTeam': 'Everyone in a team',
  'auto.recipientRole': 'Everyone with a role',
  'auto.fanOut': 'How many tasks',
  'auto.fanOutSingle': 'One task, everybody on it',
  'auto.fanOutEach': 'One task each',
  'auto.link': 'Link back to the task',
  'auto.linkNone': 'Do not link',
  'auto.excludeActor': 'Skip whoever triggered it',
  'auto.once': 'At most once per task',
  'auto.onceHint': 'Off means a second round of review asks again.',
  'auto.applyToGenerated': 'Also apply to tasks that rules created',
  'auto.applyToGeneratedHint': 'Off by default, so a rule cannot feed itself.',
  'auto.deleteConfirm': 'Delete the rule “{name}”? Tasks it already filed stay.',
  'auto.runs': 'Log',
  'auto.runsLead': 'Every decision this rule made, including the ones to do nothing.',
  'auto.noRuns': 'This rule has not run yet.',
  'auto.filed': 'filed {identifier}',
  'auto.skipNoRecipients': 'nobody to give it to',
  'auto.skipAlreadyRun': 'already done for this task',
  'auto.skipGenerated': 'that task came from a rule',
  'auto.skipNoTemplate': 'the template is gone',

  'guide.automation.title': 'Templates and rules',
  'guide.automation.lead': 'The same task, written once and filed the same way every time — by hand from a template, or by a rule that watches for something happening.',
  'guide.automation.cta': 'Open templates and rules',
  'guide.automation.label': 'A task entering review files a feedback task',
  'guide.automation.s0': 'A template is a task written in advance: a title, a description and a checklist that becomes sub-tasks.',
  'guide.automation.s1': 'A rule watches for one thing — here, a task entering the review state.',
  'guide.automation.s2': 'It files the template as a real task and links it back to the one that triggered it.',
  'guide.automation.s3': 'Who gets it is a list of selectors, not names: the lead, the people on the task, a whole team. Several at once, de-duplicated.',
  'guide.automation.s4': 'One task with everybody on it, or one each — and a log that says what it did, including when it decided to do nothing.',
  'guide.automation.h1': 'Settings, Templates and rules: write the template first, then point a rule at it.',
  'guide.automation.h2': 'Scope either to one project, or to the whole workspace so every project behaves the same.',
  'guide.automation.h3': 'Use {identifier}, {title}, {project}, {actor}, {state} and {url} in the text — they are filled in from the task that triggered it.',
  'guide.automation.h4': 'Check the log if a rule seems quiet: it records why it skipped as well as what it filed.',
  'guide.automation.newProject': 'Every new project starts with a feedback template and a rule for it, pointed at the review state and switched on.',

  'guide.autoScene.template': 'Template',
  'guide.autoScene.templateTitle': 'Feedback: {identifier} {title}',
  'guide.autoScene.when': 'when',
  'guide.autoScene.review': 'In Review',
  'guide.autoScene.made': 'Feedback: WEB-1 Redesign the pricing page',
  'guide.autoScene.logged': 'filed WEB-7 · 2 recipients',

  'guide.node.template': 'Template',
  'guide.node.templateWhat': 'A task written in advance — title, description, priority, and a checklist that becomes sub-tasks. Used by hand, or by a rule.',
  'guide.node.templateRule': 'Belongs to one project, or to the workspace so every project can use it. The text may refer to the task that triggered it.',
  'guide.node.automation': 'Rule',
  'guide.node.automationWhat': 'When one thing happens to a task — it enters a state, or it is created — file a task from a template and give it to somebody.',
  'guide.node.automationRule': 'It will not act on tasks a rule created, so it cannot feed itself. Every run is logged, including the ones that decided to do nothing.',


  /* --------------------------------------------------------------- misc */
  'misc.pageNotFound': 'Page not found',
  'misc.noWorkspaceTitle': 'No workspace yet',
  'misc.noWorkspaceHint': 'Create one in settings to get started.',
  'misc.screenUnavailableTitle': 'This screen did not arrive',
  'misc.screenUnavailableHint': 'It is fetched separately, and the network or a new version got in the way. Reloading usually settles it.',
  'misc.screenFailedTitle': 'This screen ran into an error',
  'misc.screenFailedHint': 'That is a fault in Kolibri, not in the network. The rest of the app still works — open another screen, or reload to try this one again.',
  'misc.reload': 'Reload',
} as const;

/**
 * Other locales are typed against this: same keys, but the values are plain
 * strings rather than the English literals.
 */
export type Catalogue = { readonly [K in keyof typeof en]: string };
